/**
 * SearchPage — ask a question about the estate, by time, place, subject and appearance.
 *
 * ─── TWO AXES: WHAT TO LOOK FOR, AND WHAT TO ANSWER ───
 * This page used to build a filter and always answer with the same thing: a page of detections.
 * That under-served what operators actually ask. "Which areas did the red-shirt person visit",
 * "when were they in the office", "was anyone in the server room", "where did they go, in order",
 * "did everyone wear a helmet" — each is the same kind of filter with a completely different
 * ANSWER, and making someone read two hundred detections to work out which rooms appear in them
 * is how a search feature goes unused.
 *
 * So `filters.question` picks the answer shape (and with it the aggregate), while the filter
 * controls narrow what is being asked about.
 *
 * ─── THE QUESTION COMES FROM THE TYPED SENTENCE ───
 * There is deliberately NO answer-shape picker in the filter bar. `QueryBar` parses a sentence
 * and writes the question along with the filters, so the operator asks in words rather than
 * translating their question into a control first. The parse is never hidden: the bar names the
 * question it read, the status line says what the answer will be, and every filter it set is a
 * chip that can be changed by hand.
 *
 * The consequence to know: with the bar empty the page answers with the plain list, and the other
 * five shapes are reached by typing. Two controls therefore appear only when the parsed question
 * gives them meaning — the place grouping under Scope for "which areas", and the thing being
 * measured under Looking-for for "did all of them…".
 *
 * That split is also what makes the eventual neural search a one-function swap rather than a
 * second UI: `parseQuery` returns a parse, not results, and everything downstream is unchanged.
 *
 * ─── PERSON IS NOT PERSON + FACE ───
 * One human walking past a camera running both detectors produces a person event AND a face
 * event. The subject used to search both, invisibly, so every count read high against the Person
 * Detection report and looked like a bug. "Detected by" makes the choice visible and starts on
 * person detection alone; face is added when identity matters. The narrower default comes from
 * the API (`subjects.person.defaultModels`), so an older node keeps its own behaviour.
 *
 * ─── CLOSED LISTS, WITH ONE EXCEPTION ───
 * Attribute values are always picked from a vocabulary the server published, never typed. A text
 * box invites "crimson", which matches nothing, and an empty result reads as "nobody wore red" —
 * a confident wrong answer. The one free-text field is the vehicle PLATE, which is an identity
 * rather than a category; it is canonicalised on both sides and prefix-matched.
 *
 * ─── EVERY VALUE STAYS OFFERABLE ───
 * The controls show the FULL vocabulary and use facets only for the counts beside each value. A
 * count of 0 dims but never hides: setting up "find a blue shirt" before any blue has been seen
 * is a fair question, and a palette showing two of thirteen colours reads as broken.
 *
 * ─── THE LIST IS A TRAIL FIRST, A GALLERY SECOND ───
 * The plain list has two shapes, and it opens as the TIMELINE one. Someone types a plate or a
 * name to find out where that thing WENT; a grid of cards holds that answer without presenting
 * it, because reading it means sorting two dozen timestamps by eye. `ResultTimeline` does that
 * sorting, folds the repeated detections at one camera into a single stop with a dwell, and
 * writes the movement between stops out in words — "Gate 1 09:12 · 4m later moved to Reception".
 *
 * The grid is still there behind a toggle, because it is the better shape for the other half of
 * the job ("what did these look like"), and the choice is remembered per browser.
 *
 * Both shapes render the SAME rows with the SAME indices, so the record modal and its ← / →
 * pager behave identically whichever one found the hit.
 *
 * ─── NO EVENT TIMELINE ON SEARCH ───
 * Search used to dock an "Event timeline" graph under the answer. It is gone: Search shows the
 * answer alone, and only Track docks something (the tracking path).
 *
 * ─── RBAC ───
 * The page never sends a camera allow-list. Each node's backend resolves the requester's
 * accessible cameras and applies them server-side, overwriting anything a client sends. The
 * camera and group controls can only NARROW that set; `camera_scope` in the response reports what
 * the query actually ran against, which is what lets "no results" be told from "no access" — and
 * what stops the Yes/No answer from reporting a No about a place the user cannot see.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowPathIcon,
  ArrowsUpDownIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  MapIcon,
  MapPinIcon,
  PlayCircleIcon,
  Squares2X2Icon,
  TruckIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import RecordModal from '../components/events/RecordModal';
import { useApi } from '../api/ApiProvider';
import type {
  CameraRef,
  Group,
  PersonRef,
  SearchEventRow,
  SearchFacets,
  SearchFuzziness,
  SearchPlaceBy,
  SearchResult,
  SearchSchema,
  SearchSubject,
} from '../api/contracts';
import EventListCards from '../components/events/EventListCards';
import type { EventColumnLike, EventFact } from '../components/events/EventDetail';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  ActiveChip,
  FilterDropdown,
  OptionList,
  Swatch,
  SwatchGrid,
  type FilterOption,
} from '../components/search/FilterControls';
import SearchPlaybackPanel from '../components/search/SearchPlaybackPanel';
import type { TimeRange } from '../components/search/EventTimelineStrip';
import QueryBar from '../components/search/QueryBar';
import TextMatchesBar, { activeSpellings, highlightWords } from '../components/search/TextMatchesBar';
import { examplesFor, nameExampleFor } from '../components/search/queryParser';
import ResultTimeline, { buildStops, DEFAULT_GAP_MINUTES } from '../components/search/ResultTimeline';
import TrackingPathPanel from '../components/search/TrackingPathPanel';
import { HidePaneButton, PaneRail, PaneToggles } from '../components/search/PaneControls';
import { firstTypeableName, parseQuery, type ParsedQuery } from '../components/search/queryParser';
import {
  COLOR_FIELDS,
  EMPTY_FILTERS,
  FIELD_LABEL,
  MODEL_GROUP,
  MODEL_LABEL,
  QUESTION_BY_KEY,
  SUBJECT_FALLBACK,
  SUBJECT_MODELS,
  TIME_PRESETS,
  activeCount,
  hasPlace as filtersHavePlace,
  identityAddedModels,
  identityFiltered,
  identityUnsearchable,
  modelsFor,
  resolveSubjectDef,
  summariseValues,
  titleCase,
  attrWord,
  toDsl,
  withSentence,
  withoutSentence,
  searchableText,
  type DslOptions,
  type Filters,
  type QuestionKey,
  type SubjectDef,
  type SubjectKey,
} from '../components/search/searchModel';
import WhenAnswer from '../components/search/answers/WhenAnswer';
import WhereAnswer from '../components/search/answers/WhereAnswer';
import VerdictAnswer from '../components/search/answers/VerdictAnswer';
import ComplianceAnswer from '../components/search/answers/ComplianceAnswer';
import { makePlaceNamer } from '../components/search/answers/common';
// The watch-list vocabulary, from the screens that own it — see `watchlistCategoryOptions`.
import {
  PERSON_WATCHLIST_CATEGORIES,
  VEHICLE_WATCHLIST_CATEGORIES,
} from '../components/common/watchlistForm';

/**
 * Shown inside an appearance panel when the current scope has recorded none of that attribute.
 *
 * A notice rather than a disabled control: the values stay pickable, because "nothing in this
 * window" is not the same as "this can never match". The distinction that matters is stated
 * plainly, because the two causes have completely different fixes — widen the window, or turn
 * attribute extraction on for those cameras.
 */
const NOT_COLLECTED_NOTICE =
  'Nothing recorded for this in the current scope — either no such events yet, or attribute '
  + 'extraction is not enabled on these cameras. You can still select values to search.';

/**
 * The card facts, as a synthetic column set.
 *
 * EventListCards derives a card's facts from a model's column definitions. Search rows are
 * cross-model and uniform, so instead of the per-model definitions the reports use, this is one
 * set describing the projection — which is what keeps a person event and a fire event rendering
 * as the same kind of card in one result grid.
 */
/**
 * One attribute field's value, for its own tile on a card.
 *
 * SEVERAL VALUES IS NOT ALWAYS ONE SUBJECT WEARING SEVERAL THINGS, and a card must not imply it
 * is. On `accessories` it genuinely is — one person carrying a backpack and wearing a hat. On a
 * colour it is not: a row from a node that predates the per-subject projection is a FRAME
 * carrying the union of everyone in it, so five people at a doorway make
 * `upper_color: ['black','blue','red','white','yellow']`. Printed bare, that is a card claiming
 * one person wore five tops — the specific thing this column was reported for.
 *
 * Still shown in full, because dropping four of them would be inventing a person; the hover says
 * what the list actually is. On a current node a colour arrives as one value and this never
 * fires, which is the point of having done the work server-side rather than here.
 *
 * `unknown` is KEPT, unlike the old single-line renderer which dropped it. On a tile of its
 * own "Gender — Unknown" is a real finding: the model looked and could not tell, which is not
 * the same as the field being absent, and only an empty tile ("—") should mean that.
 */
function attrCell(raw: unknown): ReactNode {
  const values = attrValues(raw);
  if (!values.length) return '—';
  const text = values.map(attrWord).join(', ');
  if (values.length === 1) return text;
  return (
    <span title={`${values.length} values — either one subject carrying several things, or, on an older node, a frame carrying every subject in it`}>
      {text}
    </span>
  );
}

/** A colour attribute with its swatch — "● Orange" is recognised before it is read. */
function colorCell(raw: unknown): ReactNode {
  const values = attrValues(raw);
  if (!values.length) return '—';
  return (
    <span className="inline-flex items-center gap-1.5">
      {values.map((v) => <Swatch key={v} value={v} className="h-3 w-3" />)}
      <span>{values.map(attrWord).join(', ')}</span>
    </span>
  );
}

/**
 * The subject's attributes as record FIELDS — what the detail modal adds to what the typed
 * table gave it.
 *
 * ── Why the modal needs handing these at all ──
 *
 * The modal shows the TYPED-TABLE record (`person_events`, `vehicle_crossing_events`), fetched
 * by the host, and that record carries the attributes only as raw `metadata` — a per-frame list
 * the AI module sent, in whatever spelling it sent it, describing everyone in the frame. The
 * normalised, vocabulary-checked, PER-SUBJECT version is the projection's, and the projection row
 * is what the search result already is. The modal is opened from one person's card, so handing it
 * that row's attributes is also the only way it shows THAT person rather than the frame. So the page hands its own row's attributes to the host's renderer rather than
 * asking it to re-derive them, which is the only arrangement in which the card and the record
 * opened FROM that card cannot disagree about what the model saw.
 *
 * Every field the subject has, including the empty ones: "Carrying —" is the record saying the
 * model looked and found nothing, and a field that simply vanished would be indistinguishable
 * from one this node does not collect.
 *
 * NOTHING at all for a row that is not this subject. A fire or intrusion event reached the
 * results through "Other models" — it has no subject and no appearance — and five "—" rows
 * under it would claim the detector looked for a shirt colour and did not find one.
 */
function attributeFacts(
  subject: SubjectKey,
  subjectDef: SubjectDef,
  row: SearchEventRow | null,
): EventFact[] {
  if (!row || row.subject_kind !== subject) return [];
  return subjectDef.fields.map((field) => ({
    key: `attr_${field}`,
    label: FIELD_LABEL[field] || titleCase(field),
    value: COLOR_FIELDS.has(field) ? colorCell(row.attributes?.[field]) : attrCell(row.attributes?.[field]),
  }));
}

/**
 * The card's facts: who, what they looked like, and which model saw them.
 *
 * ── ONE TILE PER ATTRIBUTE, not one "Appearance" line ──
 *
 * It was a single column that glued every attribute into one string — "black/blue/red top ·
 * male · backpack" — inside a cell that truncates to one line. Two things were wrong with it:
 * the reader could not tell which colour was the top and which the bottom, and on a busy frame
 * the whole cell was a run of colours with no field names at all. A tile per field is the same
 * shape the record page and the filter bar already use, so a value reads identically wherever
 * it appears.
 *
 * ── Built from `subjectDef.fields` ──
 *
 * It WAS a fixed list naming four person fields, which left two silent holes: a person's AGE
 * BAND never appeared on any card, and every vehicle row rendered "—", because a crossing
 * carries `vehicle_type`, `vehicle_color` and `direction` and the renderer looked for none of
 * them. `subjectDef.fields` is the same list the filter controls are built from and the same
 * one `/search/schema` publishes, so a field that can be filtered by can be read by, and
 * adding one server-side adds it in both places at once.
 *
 * Order is deliberate: identity first (it is what the card is about), then appearance in the
 * schema's own order, then the model last — it explains the row rather than describing it.
 */
/**
 * Whether the cards get a "Read" tile — what an OCR row says. Only where OCR rows can appear:
 * the Person page searches person and face, which never carry any.
 */
function showsReadText(subjectDef: SubjectDef): boolean {
  return subjectDef.textFields.includes('text') || subjectDef.models.includes('ocr');
}

function resultColumns(subjectDef: SubjectDef, spellings: string[] = []): EventColumnLike[] {
  return [
    { key: 'camera_id', label: 'Camera', type: 'camera' },
    {
      key: 'subject_label',
      label: 'Identity',
      // Subject-neutral since migration 050: the same column carries a person's name and a
      // vehicle's plate, so one card renderer serves both subjects.
      render: (v: string, row: any) => {
        if (v) return spellings.length ? highlightWords(v, spellings) : v;
        if (row?.subject_kind === 'vehicle') return 'No plate read';
        // An OCR frame's text row is about no one — it is the words in the picture.
        if (row?.model_key === 'ocr') return 'Text in frame';
        return row?.is_known ? 'Known' : 'Unknown';
      },
    },
    /**
     * What the frame SAID, with the matched words highlighted — the OCR text, or a plate's raw
     * read ("GJ182 / T3877"). This is how a fuzzy match is checked at a glance: the card shows
     * that "Vadal" is what was read, not "Badal", without opening the record.
     */
    ...(showsReadText(subjectDef) ? [{
      key: 'text_content',
      label: 'Read',
      render: (v: string) => (v ? highlightWords(v, spellings) : '—'),
    }] : []),
    ...subjectDef.fields.map((field) => ({
      key: field,
      label: FIELD_LABEL[field] || titleCase(field),
      // The value is on `row.attributes`, not on the row — `renderEventValue` hands the whole
      // row through as the second argument, which is the only reason this key can be the
      // attribute's own name rather than a path.
      render: (_v: unknown, row: any) => attrCell(row?.attributes?.[field]),
    })),
    { key: 'model_key', label: 'Model', render: (v: string) => MODEL_LABEL[v] || titleCase(v || '') },
  ];
}

/**
 * One attribute's values, always as a list.
 *
 * A row is one PERSON, so a person field is a one-element array — except `accessories`, which
 * genuinely holds several things one person is carrying. Vehicle fields are plain scalars. Both
 * shapes reach this table and both have to render: before this, an array went through template
 * interpolation and came out as "black,white top".
 *
 * A node that predates the per-subject projection still sends frame UNIONS here
 * (`upper_color: ["black","white"]` for two people in one row), which this renders as the list it
 * is — the honest reading of a row that describes a frame rather than a person.
 */
const attrValues = (raw: unknown): string[] =>
  (Array.isArray(raw) ? raw : raw == null ? [] : [raw])
    .filter((v): v is string => typeof v === 'string' && v.length > 0);

/**
 * The watch-list categories a subject can be filed under. All gets both lists, once each —
 * "blacklisted" exists for people and vehicles alike.
 */
function watchlistOptionsFor(subject: SubjectKey): ReadonlyArray<{ value: string; label: string }> {
  if (subject === 'vehicle') return VEHICLE_WATCHLIST_CATEGORIES;
  if (subject === 'person') return PERSON_WATCHLIST_CATEGORIES;
  const seen = new Set<string>();
  return [...PERSON_WATCHLIST_CATEGORIES, ...VEHICLE_WATCHLIST_CATEGORIES]
    .filter((c) => !seen.has(c.value) && seen.add(c.value));
}

/**
 * The filters, moved to another subject — see `chooseSubject`. Module-level because the typed
 * sentence moves the subject too, and both must carry the same things over.
 */
function convertSubject(
  prev: Filters,
  key: SubjectKey,
  subjects: Record<string, SearchSubject> | undefined,
): Filters {
  if (prev.subject === key) return prev;
  const from = resolveSubjectDef(subjects, prev.subject);
  const to = resolveSubjectDef(subjects, key);
  const next: Filters = { ...prev, subject: key, subjectModels: [], excludedTerms: [] };
  if (key === 'all') {
    next.text = [prev.personName, prev.plate, prev.text].map((s) => s.trim()).filter(Boolean).join(' ');
    next.personName = '';
    next.plate = '';
    next.known = null;
    next.otherModels = prev.otherModels.length
      ? [...new Set([...modelsFor(prev, from), ...prev.otherModels])]
      : [];
  } else {
    if (to.textFilter === 'plate') { next.personName = ''; next.known = null; } else next.plate = '';
    next.otherModels = prev.otherModels.filter((m) => !SUBJECT_MODELS.has(m));
  }
  if (prev.splitBy && !to.fields.includes(prev.splitBy.field)) next.splitBy = null;
  const allowed = new Set(watchlistOptionsFor(key).map((c) => c.value));
  next.watchlistCategories = prev.watchlistCategories.filter((c) => allowed.has(c));
  return next;
}

/** The order of the "Looking for" switch on the merged page. */
const SUBJECT_ORDER: SubjectKey[] = ['all', 'person', 'vehicle'];

/** One line under the switch, saying what that choice searches. */
const SUBJECT_HINT: Record<SubjectKey, string> = {
  all: 'Every model — names, plates and text read on camera.',
  person: 'People by name, enrolment or what they wore.',
  vehicle: 'Vehicles by plate, type, colour or direction.',
};

/** One titled block of the filter panel. */
function FilterSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

/**
 * A text filter typed straight into the panel — name, plate or free text.
 *
 * Holds its own draft and commits it after a pause, because every committed value is a query:
 * committing per keystroke sent "b", "ba", "bad"… to the server. Follows the filter when it
 * changes from elsewhere (a parsed sentence, a chip's ×), so the box never shows a stale word.
 */
function TextFilter({
  label, value, onChange, placeholder, hint, uppercase = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  uppercase?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const commit = useRef(onChange);
  commit.current = onChange;
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    if (draft === value) return undefined;
    const t = setTimeout(() => commit.current(draft), 400);
    return () => clearTimeout(t);
    // `value` deliberately absent: a commit changes it, and that must not restart the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  return (
    <label className="block">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className="relative mt-0.5 block">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit.current(draft); }}
          placeholder={placeholder}
          className={[
            'w-full rounded-md border bg-background py-1.5 pl-2 pr-7 text-xs text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none',
            value.trim() ? 'border-brand' : 'border-border',
            uppercase ? 'uppercase placeholder:normal-case' : '',
          ].join(' ')}
        />
        {draft && (
          <button
            type="button"
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => { setDraft(''); commit.current(''); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
      {hint && <span className="mt-0.5 block text-[10px] leading-snug text-muted-foreground">{hint}</span>}
    </label>
  );
}

/** A small labelled radio row — for choices short enough to show all at once. */
function Segmented({
  label, options, value, onChange,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <div
        className="mt-0.5 grid overflow-hidden rounded-md border border-border"
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
        role="radiogroup"
        aria-label={label}
      >
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            onClick={() => onChange(o.value)}
            className={[
              'truncate px-1.5 py-1 text-[11px] transition-colors',
              value === o.value ? 'bg-brand font-medium text-white' : 'text-foreground hover:bg-muted',
            ].join(' ')}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** How the place buckets are labelled in the "Which areas?" control. */
const PLACE_BY_OPTIONS: Array<{ value: SearchPlaceBy; label: string; hint: string }> = [
  { value: 'group', label: 'Area (camera group)', hint: 'Named, nested areas — how people describe a site.' },
  { value: 'camera', label: 'Camera', hint: 'One row per camera. No nesting, so the rows add up.' },
  { value: 'zone', label: 'Zone within a view', hint: 'Regions of interest, where the cameras have them configured.' },
];

/**
 * "This page does not answer that — here is the one that does."
 *
 * One component for both misses (wrong answer shape, wrong subject) because they are the same
 * promise: say plainly what was read, say plainly what was done with it instead, and offer the
 * page that would have answered it. Two hand-written banners would have drifted into two
 * different tones for one situation, and the tone is most of what makes this readable rather
 * than alarming — nothing has gone wrong, the operator is one click from the right page.
 *
 * `brand-light`, not amber: this is a redirection, not a warning.
 */
/** N columns, drawn — the cards-per-row control's options read as layouts, not as numbers. */
function ColumnsGlyph({ n }: { n: number }) {
  const gap = 1.5;
  const w = (14 - gap * (n - 1)) / n;
  return (
    <svg viewBox="0 0 14 14" className="h-3.5 w-3.5" aria-hidden>
      {Array.from({ length: n }, (_, i) => (
        <rect key={i} x={i * (w + gap)} y={2} width={w} height={10} rx={1} fill="currentColor" />
      ))}
    </svg>
  );
}

/**
 * The Track results column's width: dragged, remembered, and clamped to THIS window so a width
 * dragged out on a wide monitor cannot squeeze the map to nothing on a laptop.
 */
const TRACK_RESULTS_MIN_PX = 300;
/** What the map keeps, whatever the results column is dragged to. */
const TRACK_MAP_MIN_PX = 320;
function clampTrackWidth(w: number, room = window.innerWidth): number {
  return Math.round(Math.max(TRACK_RESULTS_MIN_PX, Math.min(w, room - TRACK_MAP_MIN_PX, room * 0.65)));
}
function defaultTrackWidth(): number {
  // Two stop cards side by side on an ordinary laptop, three on a wide screen.
  return clampTrackWidth(Math.min(560, Math.max(360, window.innerWidth * 0.3)));
}

function SwitchNotice({
  message,
  action,
}: {
  message: string;
  /** Absent when the host gave no `onSwitch` — the notice still says what happened. */
  action?: { label: string; onClick: () => void } | false;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-brand-light px-4 py-2 text-xs text-foreground">
      <span>{message}</span>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="rounded-md border border-brand px-2 py-0.5 font-medium text-brand transition-colors hover:bg-brand hover:text-white"
        >
          Open {action.label}
        </button>
      )}
    </div>
  );
}

// ─── Props ─────────────────────────────────────────────────────────────────

export interface SearchPageProps {
  /**
   * Which reading the page OPENS with — see "TWO PAGES, ONE ENGINE" above. A seed, not a lock:
   * the header toggle and a typed sentence both change it. Default `search`.
   */
  mode?: SearchPageMode;
  /** Changes when the operator switches node (cloud); resets results and refetches lookups. */
  nodeKey?: string;
  /** A result's frame as a directly-loadable URL (local). Return null to fall back to loadImage. */
  imageFor?: (row: SearchEventRow) => string | null;
  /** Lazy frame resolver, for hosts that must stream images through a tunnel (cloud). */
  loadImage?: (row: SearchEventRow) => Promise<string | null>;
  /**
   * Open one result in the host's routed event-detail page.
   *
   * With `eventDetail` supplied this is no longer what a card click does — it becomes the
   * modal's "Full page" escape hatch, for the times the record has to outlive the search that
   * found it (a link in a ticket, a bookmark, a second monitor).
   */
  onOpenEvent?: (row: SearchEventRow) => void;
  /**
   * Open a result in a MODAL over the results, rather than navigating away.
   *
   * A search is a position an operator is working through — the same relationship a report table
   * has to its rows, which is why those open a modal too. Navigating to a page throws the query,
   * the answer and the scroll position away to look at one hit, and coming back means rebuilding
   * all three. So a card opens a modal, and ← / → step to the next hit without closing it.
   *
   * Grouped into one prop because the three parts are useless apart, and split this way because
   * of where the knowledge lives:
   *   `load`   — a search row comes from the PROJECTION and carries only what the index needs
   *              (id, camera, timestamp, attributes, frames). The detail body wants the
   *              typed-table record, so it has to be fetched, and only the host knows how.
   *   `render` — `EventDetailBody` is host-local (each app loads snapshots differently), so the
   *              body cannot be built here. Handed the page's own camera-name map so the record
   *              names cameras exactly as the cards above it do, and without a second fetch.
   *   `title`  — likewise.
   *
   * Omit it and a card click falls back to `onOpenEvent`, which is what this page did before.
   */
  eventDetail?: {
    load: (row: SearchEventRow) => Promise<any>;
    render: (
      event: any,
      row: SearchEventRow,
      ctx: {
        cameraNames: Record<string, string>;
        /**
         * The subject's attributes, already rendered, for the host to append to the record's
         * own fields. See `attributeFacts` — the typed-table record the host fetched does not
         * carry the normalised set, and re-deriving it there would let the modal and the card
         * that opened it disagree.
         */
        attributeFacts: EventFact[];
      },
    ) => ReactNode;
    title?: (event: any, row: SearchEventRow) => ReactNode;
  };
  /**
   * Offer the per-result Playback control. Default on.
   *
   * The player itself is shared now (`RecordingPlaybackPlayer`), so no host has to supply one —
   * this exists only for a host that should not offer recording playback at all.
   */
  playback?: boolean;
  /** Longer HLS timeouts, for a stream crossing the cloud's WebSocket tunnel. */
  playbackTunnelled?: boolean;
  /** Jump to the full Recordings page at this camera and moment. */
  onOpenRecordings?: (row: SearchEventRow, fromIso: string, toIso: string) => void;
  /** Rendered instead of the page when the host has nothing to query (cloud: no node selected). */
  emptyState?: ReactNode;
  /**
   * Lock this instance to one subject — see "…AND THE SUBJECT IS THE SECOND AXIS" above.
   *
   * Omit it and the in-page subject switch appears, which is what this page did before.
   */
  subject?: SubjectKey;
  /**
   * The subject an UNLOCKED page opens on — All, Person or Vehicle. A seed, like `mode`: the
   * "Looking for" switch in the filter panel and a typed sentence both change it. Default `all`.
   */
  initialSubject?: SubjectKey;
  /** The subject changed inside an unlocked page — for the host to reflect in the URL. */
  onSubjectChange?: (subject: SubjectKey) => void;
  /**
   * A sentence to apply on arrival, from the host's `?q=`.
   *
   * This is what makes the two "answered on the other page" notices a FIX rather than a
   * signpost: the operator typed a plate into Person Search, and sending them to Vehicle Search
   * with an empty box would make them type it again — at which point the notice has cost them
   * more than it saved.
   */
  initialQuery?: string;
  /**
   * Filters to open WITH, for a link that already knows what it is asking about.
   *
   * The Watch List's "Track" button is why this exists. It used to be tempting to hand the
   * subject over as a typed sentence in `?q=`, but the parser recognises a name only if it is in
   * the registry it was given — so a label it did not know produced NO filter at all, and the
   * operator landed on an unfiltered page that reads as "this is their whole trail". A link that
   * knows the plate or the name must say so in terms the page cannot misread.
   *
   * Applied once, at mount, and only over fields the UI can SHOW: whatever arrives here appears
   * as a removable chip like any other filter, so nothing is narrowing the answer invisibly.
   */
  initialFilters?: Partial<Filters>;
  /**
   * Take the operator to the OTHER subject's page, carrying the sentence they typed.
   *
   * Only the subject, because the mode is no longer a page — a sentence asking for a trail
   * flips the toggle in place. Supplied by the host because only it knows the routes; without
   * it the notice still says what happened, it just cannot offer the one-click fix.
   */
  onSwitch?: (target: { subject: SubjectKey; query?: string }) => void;
  /**
   * The mode changed inside the page — the toggle, or a typed sentence that asked for a trail.
   *
   * For the host to reflect in the URL, so the reading on screen can be linked and survives a
   * reload. Purely an output: `mode` seeds the page and is never pushed back into it, or a
   * host that re-rendered on the URL change would fight the toggle.
   */
  onModeChange?: (mode: SearchPageMode) => void;
}

// ─── Page ──────────────────────────────────────────────────────────────────

/**
 * Detections per page.
 *
 * A HUNDRED, NOT TWENTY-FOUR, and the reason is the fold. The list groups consecutive detections
 * of one subject at one camera into a single STOP, and a camera running at several frames a
 * second makes forty detections out of one person walking through a doorway. Twenty-four rows is
 * therefore not twenty-four stops — it can be one — so a month-long search for somebody seen on
 * seven different days came back showing the last two of them, and read as missing data rather
 * than as page one.
 *
 * A hundred is what the server will send without complaint (its ceiling is 500) and what the
 * card track draws without a pause. Past that the pager takes over, which is what a pager is
 * for: the alternative — sending every match — makes the first screen wait on the last row.
 */
const PAGE_SIZE = 100;

/**
 * The row budget per question, because the aggregates page different things.
 *
 * `journey` pages SUBJECTS, not rows, and each one expands into a whole trail — twenty is
 * already a long scroll, and a hundred would be a hundred trails. `attribute_split` uses the
 * limit for its named "without" list, where fifty is enough to be a roster rather than a sample.
 * The place and timeline aggregates are bounded by the estate and the window, so their limit is
 * never reached.
 */
function limitFor(question: QuestionKey): number {
  if (question === 'compliance') return 50;
  return PAGE_SIZE;
}

/**
 * The clip played for an event: 10 s before it, 10 s after — twenty seconds, not the five-minute
 * segment the footage lives in.
 *
 * Centred on the detection: a person is usually already mid-frame by the time a model fires, so
 * the ten seconds before show them arrive, and the ten after cover the moment itself.
 *
 * The panel keeps playback inside that clip (see SearchPlaybackPanel) and offers the full
 * Recordings timeline for anything beyond it.
 */
const PLAYBACK_LEAD_SEC = 10;
const PLAYBACK_TAIL_SEC = 10;
/** How much wider than the clip to ask for segments — more than one 5-minute segment. */
const SEGMENT_PAD_MS = 6 * 60 * 1000;

/**
 * ─── HOW THE PLAIN LIST IS DRAWN IS NOT A CHOICE ANY MORE ───
 *
 * It was a remembered Timeline / Grid toggle. It is now decided by the MODE, because the two
 * shapes turned out to be the two modes:
 *
 *   SEARCH → detail cards. The question is "which detections match, and what did they look
 *            like", so every card carries its frame and every attribute the subject has. The
 *            folded timeline actively hid that: it collapses forty detections at one camera
 *            into one stop, which is the right answer to a different question and throws away
 *            the per-detection appearance that the whole attribute vocabulary exists for.
 *   TRACK  → the folded timeline. One stop per camera visit, in order, with the movement
 *            between stops written out — which IS following somebody.
 *
 * A toggle offering both inside one mode was the page asking the operator to re-state, as a
 * layout, the thing they had already said by choosing Search or Track.
 */
type TrailOrder = 'desc' | 'asc';

/**
 * ─── TWO PAGES, ONE ENGINE — AND THE PAGE IS THE SUBJECT ───
 *
 * There are two axes here and only one of them can be the route:
 *
 *   SUBJECT (person / vehicle) decides WHAT THE PAGE CAN ASK. A garment colour is meaningless
 *   on an ANPR event and a plate is meaningless on a person, so switching it rebuilds the
 *   filter bar and invalidates the query in progress. That is a different page.
 *
 *   MODE (search / track) decides WHAT THE ANSWER LOOKS LIKE. Every filter, every vocabulary
 *   and every fetch is identical either way — "which of these people" and "where did that
 *   person go" are the same query read two ways — so flipping it costs the operator nothing
 *   and should cost them nothing: no navigation, no re-typing, no lost filters.
 *
 * So the subject is the route and the mode is a toggle in the header. Four pages were three too
 * many: the pair that differed only in how the results were DRAWN made an operator who had set
 * up a query and then wanted the other reading of it navigate away and build it again.
 *
 * `mode` is therefore STATE, seeded from the route so a link can open either reading, and
 * reported back to the host so the URL follows. It is not a lock: a sentence asking for a trail
 * flips it, which is the whole point of the two living together.
 */
export type SearchPageMode = 'search' | 'tracking';

/**
 * Which mode ANSWERS each shape.
 *
 * `list` is absent because it belongs to both — the set of matching detections is the search
 * answer AND what the Track tab folds into a timeline — so it leaves the mode alone rather than
 * flipping it under a typed sentence that did not ask to move.
 *
 * `journey` still names Track, and that is now the ONLY thing it does: the sentence "trail of
 * the red shirt person" is an instruction to FOLLOW, so it belongs on Track — but Track answers
 * it with the timeline like everything else (see `ANSWERABLE`).
 */
const MODE_OF_QUESTION: Partial<Record<QuestionKey, SearchPageMode>> = {
  when: 'search',
  where: 'search',
  verdict: 'search',
  compliance: 'search',
  journey: 'tracking',
};

/**
 * ─── THE PER-SUBJECT TRAIL IS GONE ───
 *
 * Track answered with one card per subject, each holding that subject's own route, and the page
 * was built around picking an individual out of them. It is now the TIMELINE for everybody: one
 * list of stops in time order, folded per camera visit, which is the same shape Track had for
 * its drill-down and the shape the path dock draws.
 *
 * Why: the trail cards asked the operator to choose a subject before the page would show them a
 * route, and on the tab whose whole job is "where did this go" that choice is one they have
 * usually already made with the filters. Two answers to one question, and the one that needed a
 * click first was the one in the way.
 *
 * `journey` is therefore not in this list. A sentence that parses to one still lands on Track —
 * that is what it was asking for — and is answered here.
 */
const ANSWERABLE: QuestionKey[] = ['list', 'when', 'where', 'verdict', 'compliance'];

/**
 * A shape this page does not draw becomes the plain list, which every mode can render.
 *
 * NOT called `answerable`: the component has a local flag of that name for a different question
 * — whether the FILTERS can answer the shape at all (a Yes/No with nowhere to check). A
 * module-level function of the same name is shadowed inside the component and calling it throws
 * at runtime, which is the trap that already cost this file a release once (see `typeFilter`).
 */
const drawableQuestion = (q?: QuestionKey): QuestionKey => (q && ANSWERABLE.includes(q) ? q : 'list');

/** What each mode asks when nothing more specific was typed. */
const DEFAULT_QUESTION: Record<SearchPageMode, QuestionKey> = {
  search: 'list',
  tracking: 'list',
};

/**
 * ─── …AND THE SUBJECT IS THE SECOND AXIS ───
 *
 * `subject` locks an instance to people or to vehicles, the same way `mode` locks it to finding
 * or following. Four pages, still one engine.
 *
 * WHY IT IS A PAGE AND NOT A TAB. The subject decides which questions are even askable — a
 * garment colour is meaningless on an ANPR event and a plate is meaningless on a person — so
 * half the filter bar changed under the operator when they touched the switch, and the page they
 * had set up was gone. It is also the axis people arrive with already decided: nobody opens this
 * page wondering whether they are looking for a man or a van. A route per subject means the two
 * are bookmarkable, linkable and separately remembered, and the filter bar stops offering
 * controls that cannot apply.
 *
 * It stays OPTIONAL. Left off, the in-page subject switch renders exactly as it did — the
 * component is still able to do both, which is what keeps this a mounting decision rather than a
 * fork of the engine.
 */
type Chrome = { title: string; tagline: string; question: QuestionKey };

const MODE_CHROME: Record<SearchPageMode, Chrome> = {
  search: {
    title: 'Search',
    tagline: 'Find people and vehicles by identity or appearance',
    question: 'list',
  },
  tracking: {
    title: 'Tracking',
    tagline: 'Follow a person or vehicle across the estate, in the order they moved',
    question: 'list',
  },
};

/**
 * The titles for a subject-locked instance.
 *
 * Named for the SUBJECT first ("Person Search", not "Search — Person"), because that is how the
 * sidebar reads them and how an operator refers to them out loud. The taglines drop the "and
 * vehicles" half the unlocked copy needs: a page that answers about one of the two must not keep
 * advertising the other, or the first plate typed into Person Search is the operator following
 * the page's own promise into an empty result.
 */
const SUBJECT_CHROME: Record<SearchPageMode, Record<SubjectKey, Omit<Chrome, 'question'>>> = {
  search: {
    person: { title: 'Person Search', tagline: 'Find people by name, enrolment or appearance' },
    vehicle: { title: 'Vehicle Search', tagline: 'Find vehicles by plate, type, colour or direction' },
    all: { title: 'Search Everything', tagline: 'Find any name, plate or word read on camera — spelling mistakes included' },
  },
  tracking: {
    person: { title: 'Person Tracking', tagline: 'Follow one person across the estate, in the order they moved' },
    vehicle: { title: 'Vehicle Tracking', tagline: 'Follow one vehicle across the estate, in the order it moved' },
    all: { title: 'Track Anything', tagline: 'Follow a name or a plate across the estate, in the order it was seen' },
  },
};

/**
 * What each page is called in the sidebar and in the notice that offers it.
 *
 * The bare subject, because the page is both readings of it — "Open Vehicle Search" would name
 * one of the two things the operator is about to be given. The sidebar agrees: these two are the
 * children of a "Search & Track" group, and the group carries the both-readings half of the name
 * so each leaf can stay the bare subject.
 */
const SUBJECT_PAGE_NAME: Record<SubjectKey, string> = { person: 'Person', vehicle: 'Vehicle', all: 'All' };

function chromeFor(mode: SearchPageMode, subject?: SubjectKey): Chrome {
  const base = MODE_CHROME[mode];
  if (!subject) return base;
  return { ...base, ...SUBJECT_CHROME[mode][subject] };
}

/**
 * Namespaced by SUBJECT, not by mode.
 *
 * The result layout is no longer remembered at all — it follows the mode now — so the trail
 * order is the only preference left. It is keyed per subject because reading a person's day and
 * a vehicle's day are different habits, and not per mode because only one mode draws a trail.
 */
const orderKey = (subject?: SubjectKey) => `trinetra.${subject || 'any'}.trailOrder`;

/**
 * A remembered preference, read once at mount.
 *
 * Wrapped because `localStorage` THROWS rather than returning null in a private window and under
 * some enterprise policies — and a page that fails to render because it could not remember a
 * layout is a much worse bug than a page that forgets one.
 */
function remembered<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const saved = localStorage.getItem(key) as T | null;
    if (saved && allowed.includes(saved)) return saved;
  } catch {
    /* private mode — nothing was ever remembered, and nothing can be */
  }
  return fallback;
}

function remember(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode — the choice just will not survive a reload */
  }
}

export default function SearchPage({
  mode: initialMode = 'search',
  onModeChange,
  subject: lockedSubject,
  initialSubject = 'all',
  onSubjectChange,
  initialQuery,
  initialFilters,
  nodeKey = '',
  imageFor,
  loadImage,
  onOpenEvent,
  eventDetail,
  playback = true,
  playbackTunnelled = false,
  onOpenRecordings,
  emptyState,
  onSwitch,
}: SearchPageProps) {
  const { search, wallPlayback } = useApi();

  /**
   * Search or Track. STATE, not a prop, because the two are one page now — the route only
   * SEEDS it, so a link can open either reading, and `onModeChange` lets the host put it back
   * in the URL. Nothing about the query changes when it flips; only the answer shape does.
   */
  const [mode, setMode] = useState<SearchPageMode>(initialMode);
  const chrome = chromeFor(mode, lockedSubject);
  /** Where an unlocked page starts, and what it resets to. The route's lock wins when there is one. */
  const startSubject: SubjectKey = lockedSubject || initialSubject;

  /**
   * This page's starting filters — the answer shape from the mode, the subject from the route.
   *
   * The subject belongs in here and not only in the initial state because `emptyFilters` is what
   * "Clear all", the query bar's clear, and every parse reset back to. A locked page that
   * cleared itself onto the OTHER subject would be the one gesture that escapes the lock.
   */
  const emptyFilters = useMemo<Filters>(
    () => ({
      ...EMPTY_FILTERS,
      question: chrome.question,
      subject: startSubject,
    }),
    [chrome.question, startSubject],
  );

  /**
   * THE LINK'S OWN TERMS, CAPTURED ONCE.
   *
   * A ref rather than the prop, because the page RESETS its filters after mount — the lookup
   * effect below clears everything before fetching the schema, cameras and groups, and on the
   * cloud it does so again whenever the node changes. Seeding only the initial state therefore
   * looked right for a frame and then went blank: the Watch List's Track button landed on an
   * unfiltered page, which is exactly the failure the explicit filters were meant to prevent.
   *
   * A ref also makes the prop's identity irrelevant — a host that builds the object inline gets
   * the same behaviour as one that memoises it.
   */
  const seedFilters = useRef(initialFilters);

  const [filters, setFilters] = useState<Filters>(() => ({
    ...EMPTY_FILTERS,
    question: DEFAULT_QUESTION[initialMode],
    subject: startSubject,
    // Last, so a link's own terms win over the defaults — but never over the locked subject,
    // which is the route itself and not a filter.
    ...initialFilters,
    ...(lockedSubject ? { subject: lockedSubject } : null),
  }));
  const [page, setPage] = useState(0);

  const [schema, setSchema] = useState<SearchSchema | null>(null);
  const [facets, setFacets] = useState<SearchFacets>({});
  const [cameras, setCameras] = useState<CameraRef[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  /**
   * The registered people, for recognising a name in a typed sentence.
   *
   * Empty is a normal state, not a failure: the host may not implement the lookup, and listing
   * the registry needs a permission a search user can lack. The Name control keeps working
   * either way — that filter is applied to the event rows server-side and never touches this
   * list. Only the typed shortcut goes quiet.
   */
  const [people, setPeople] = useState<PersonRef[]>([]);

  /**
   * The last text parse, or null.
   *
   * Cleared the moment a control is touched by hand, because the "Read as" row is a claim about
   * what the current query IS. A stale claim beside filters that have since changed is worse than
   * no claim at all.
   */
  const [parsed, setParsed] = useState<ParsedQuery | null>(null);
  /**
   * The sentence behind `parsed`, kept so the two "answered elsewhere" notices can carry it to
   * the page that does answer it. `ParsedQuery` holds what was UNDERSTOOD, not what was typed,
   * and re-serialising the parse back into words would send the operator a sentence they did
   * not write.
   */
  const [queryText, setQueryText] = useState('');
  /**
   * What the last typed sentence CONTRIBUTED to the filters — so the next sentence (or an
   * emptied box) can take exactly that back out and leave everything picked by hand alone.
   * See `withSentence`. A ref: it is bookkeeping for the next apply, never drawn.
   */
  const sentenceLayer = useRef<Partial<Filters> | null>(null);

  /** The custom date-range inputs under the time presets — shown on demand. */
  const [customOpen, setCustomOpen] = useState(() => !!initialFilters?.time?.from);

  /** The row whose footage is open in the docked panel, or null. */
  const [playbackRow, setPlaybackRow] = useState<SearchEventRow | null>(null);

  /**
   * How to draw the plain list, and which way to read the trail. See `ListView`.
   *
   * Page state rather than part of `Filters`: neither changes the QUERY, so putting them there
   * would make every layout flip re-issue the search and re-fetch the facets and the strip.
   */
  const [trailOrder, setTrailOrder] = useState<TrailOrder>(
    () => remembered(orderKey(lockedSubject), ['desc', 'asc'] as const, 'desc'),
  );

  const toggleOrder = useCallback(() => {
    setTrailOrder((prev) => {
      const next = prev === 'desc' ? 'asc' : 'desc';
      remember(orderKey(lockedSubject), next);
      return next;
    });
  }, [lockedSubject]);

  /**
   * Flip between the two readings of the query on screen.
   *
   * It moves the QUESTION with it, because a mode that did not would be a title: Track still
   * answering "which areas" is Search wearing the other name. A question the target mode
   * already owns is KEPT — switching to Search from a "which areas" sentence must not throw
   * that sentence away — and only a question belonging to the mode being left is replaced.
   *
   * Nothing else is touched. Every filter, the window and the typed sentence all survive,
   * which is the entire reason these two stopped being separate pages.
   */
  const chooseMode = useCallback((next: SearchPageMode) => {
    setMode(next);
    setPage(0);
    onModeChange?.(next);
    setFilters((prev) => (MODE_OF_QUESTION[prev.question] === next || prev.question === 'list'
      ? prev
      : { ...prev, question: DEFAULT_QUESTION[next] }));
  }, [onModeChange]);

  /**
   * The open record, as an INDEX into the rows on screen rather than the row itself.
   *
   * An index is what lets ← / → step to the next hit: the pager moves the index and the modal
   * follows. Holding the row instead would make "next" impossible without also holding where it
   * came from.
   */
  // The open record's INDEX only. Fetching it, the loading and error states, and the race a
  // pager creates all live in RecordModal now — see its header.
  const [detailIndex, setDetailIndex] = useState<number | null>(null);

  /**
   * Event rows for the Yes/No answer's evidence, fetched separately.
   *
   * The verdict runs on `by_place`, so `result.rows` are PLACES — they carry no event id, camera
   * or timestamp, and handing them to the card grid would render a row of broken cards. The two
   * cannot come from one call: the place aggregate is what gives the exact first and last sighting
   * across the whole match set, while a `list` page would only ever know about its own page. So a
   * Yes gets one extra request for the frames, which is the one question where seeing them is the
   * point.
   */
  const [evidenceRows, setEvidenceRows] = useState<SearchEventRow[]>([]);

  const [result, setResult] = useState<SearchResult | null>(null);
  /**
   * WHICH QUESTION the answer in `result` is an answer to.
   *
   * The six aggregates return six different row shapes, and `result` outlives the question that
   * produced it: changing the question re-runs the query, but the previous answer stays on
   * screen until the new one lands (deliberately — blanking the page on every filter keystroke
   * is worse). For one render, therefore, the page holds a question and a result that disagree,
   * and the answer component for the NEW question is handed the OLD question's rows.
   *
   * That is a crash, not a flicker, wherever an answer reads a field only its own shape has —
   * the trail view read `row.stops.length` off plain detections and took the whole page down.
   * That view is gone, but places, buckets and split subjects are still three more shapes
   * behind one `rows`, so the guard stays.
   */
  const [resultQuestion, setResultQuestion] = useState<QuestionKey | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Narrow a filter dimension and return to page 1 — a filter change invalidates the offset. */
  const set = useCallback(<K extends keyof Filters>(key: K, value: Filters[K]) => {
    setPage(0);
    setParsed(null);
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // A range is a slice of a WINDOW. Moving the window leaves the slice describing a time the
      // query no longer covers, so it goes with it rather than silently emptying the results.
      ...(key === 'time' ? { range: null } : null),
      // Struck-out spellings belong to the words that produced them. New words, new spellings —
      // a stale strike-out would silently remove a match the operator never saw offered.
      ...(key === 'text' || key === 'personName' || key === 'plate' ? { excludedTerms: [] } : null),
    }));
  }, []);

  /** Strike a spelling out of the fuzzy match, or put it back. */
  const toggleSpelling = useCallback((term: string) => {
    setPage(0);
    setFilters((prev) => {
      const t = term.toLowerCase();
      const on = prev.excludedTerms.some((x) => x.toLowerCase() === t);
      return {
        ...prev,
        excludedTerms: on ? prev.excludedTerms.filter((x) => x.toLowerCase() !== t) : [...prev.excludedTerms, term],
      };
    });
  }, []);

  /** The strip's selection. Its own setter, because a range never changes the page's own filters. */
  const setRange = useCallback((range: TimeRange | null) => {
    setPage(0);
    setParsed(null);
    setFilters((prev) => ({ ...prev, range }));
  }, []);

  const setAttribute = useCallback((field: string, values: string[]) => {
    setPage(0);
    setParsed(null);
    setFilters((prev) => ({ ...prev, attributes: { ...prev.attributes, [field]: values } }));
  }, []);

  const cameraNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of cameras) m.set(c.id, c.name);
    return m;
  }, [cameras]);

  const groupNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups as any[]) m.set(g.id, g.name);
    return m;
  }, [groups]);

  const namePlace = useMemo(() => makePlaceNamer(cameras, groups), [cameras, groups]);

  /**
   * The same names, as the plain object both hosts' `EventDetailBody` takes.
   *
   * Derived from the Map rather than fetched again: the detail modal must name a camera the same
   * way the card behind it does, and two lookups of the same list are two chances to disagree.
   */
  const cameraNameMap = useMemo(
    () => Object.fromEntries(cameraNames) as Record<string, string>,
    [cameraNames],
  );

  // ── Lookups. Re-fetched per node; failures are non-fatal so a missing camera list degrades
  //    the controls to ids rather than breaking search itself.
  useEffect(() => {
    let live = true;
    setSchema(null); setCameras([]); setGroups([]); setPeople([]); setResult(null);
    // The link's terms survive this reset — see `seedFilters`. The locked subject is applied
    // last either way: it is the route, not a filter, and no link may contradict it.
    setFilters({
      ...emptyFilters,
      ...seedFilters.current,
      ...(lockedSubject ? { subject: lockedSubject } : null),
    });
    setPage(0); setParsed(null); sentenceLayer.current = null;

    (async () => {
      const [s, c, g, p] = await Promise.allSettled([
        search.schema(), search.listCameras(), search.listGroups(),
        // Optional on the adapter, and allowed to reject: a user with search access but not
        // `ai_events.view` cannot list the registry, and that must cost them the name shortcut
        // rather than the page.
        search.listPeople ? search.listPeople() : Promise.resolve([] as PersonRef[]),
      ]);
      if (!live) return;
      if (s.status === 'fulfilled') setSchema(s.value);
      if (c.status === 'fulfilled') setCameras(c.value || []);
      if (g.status === 'fulfilled') setGroups(g.value || []);
      if (p.status === 'fulfilled') setPeople(p.value || []);
    })();

    return () => { live = false; };
  }, [search, nodeKey]);

  const subjects = schema?.subjects;
  const subjectDef = useMemo(
    () => resolveSubjectDef(subjects, filters.subject),
    [subjects, filters.subject],
  );

  /**
   * Change what the page is looking for, carrying over whatever still means something.
   *
   * The subjects do not share an identity box: All has only free text, Person a name, Vehicle a
   * plate. A name or plate moving to All becomes words in the text (which is what All searches
   * them as); moving between Person and Vehicle drops the other's box, because a plate is not a
   * name and leaving it would be a chip the query never sends.
   *
   * The model filter changes meaning too. On All, "Types" IS the model list; on a subject it
   * ADDS models beside that subject's own. So the set actually searched is what gets carried,
   * never the raw list — "person + fire" must not arrive on All as "fire only".
   */
  const chooseSubject = useCallback((key: SubjectKey) => {
    setPage(0);
    setParsed(null);
    setFilters((prev) => convertSubject(prev, key, subjects));
  }, [subjects]);

  /**
   * Report the subject to the host, whichever way it changed — the switch, or a sentence that
   * named a person or a vehicle. Through a ref, so a host passing a fresh closure each render
   * does not re-fire it.
   */
  const subjectChangeRef = useRef(onSubjectChange);
  subjectChangeRef.current = onSubjectChange;
  useEffect(() => {
    if (!lockedSubject) subjectChangeRef.current?.(filters.subject);
  }, [filters.subject, lockedSubject]);

  const questionSpec = QUESTION_BY_KEY[filters.question] || QUESTION_BY_KEY.list;

  /** The answer on screen was computed for the question on screen. See `resultQuestion`. */
  const answerReady = !!result && resultQuestion === filters.question;
  /** There is an answer, but to the question BEFORE this one. It must not be drawn. */
  const staleShape = !!result && resultQuestion !== filters.question;

  /**
   * The stops the path dock draws — the SAME fold the timeline above it prints.
   *
   * `buildStops` is ResultTimeline's own, exported for this: a second fold with its own idea of
   * where a visit ends would put a pin on the plan for a stop that is not in the list beside it.
   * Time order, oldest first — the list's reading direction is applied after the fold, and a
   * route is what happened rather than how it is being read.
   *
   * Only for the plain list: every other answer's rows are places, subjects or buckets, none of
   * which fold into stops. `answerReady` keeps the shape honest.
   */
  const pathStops = useMemo(
    () => (answerReady && filters.question === 'list'
      ? buildStops((result?.rows || []) as SearchEventRow[], DEFAULT_GAP_MINUTES * 60_000).stops
      : []),
    [answerReady, filters.question, result],
  );

  /**
   * The SUBJECT the sentence named, when this page is not about it — a plate typed into Person
   * Search, "the man in the red shirt" typed into Vehicle Tracking.
   *
   * The same bargain as `askedElsewhere`, for the same reason: answering it here is impossible
   * (the two subjects share no attribute and no identity field), and answering the nearest
   * question instead would hand back a real-looking result for a query nobody made.
   */
  const askedOtherSubject = lockedSubject && parsed?.filters?.subject
    && parsed.filters.subject !== lockedSubject
    ? parsed.filters.subject
    : null;
  const limit = limitFor(filters.question);

  /**
   * How this node wants the query: fuzzy text when its schema advertises it (an older node
   * rejects an unknown `text` field), and NEWEST-first in Track mode — the timeline folds
   * consecutive detections into stops, which a relevance-ordered page does not have.
   */
  const dslOpts = useMemo<DslOptions>(() => ({
    textSearch: !!schema?.text_search,
    sort: mode === 'tracking' ? 'newest' : undefined,
  }), [schema, mode]);
  const textSearch = !!dslOpts.textSearch;

  const dsl = useMemo(
    () => toDsl(filters, subjectDef, limit, page * limit, dslOpts),
    [filters, subjectDef, limit, page, dslOpts],
  );

  // ── Facets follow the SCOPE only (time/camera/group/model), never the appearance selection:
  //    they describe what is available to pick, so narrowing by one colour must not hide the rest.
  //    The dragged RANGE is part of that scope, and has to be in this key: `toDsl` already sends
  //    it, so leaving it out left the counts describing the whole window while the query ran
  //    against one hour of it.
  const scopeKey = useMemo(() => JSON.stringify({
    t: filters.time, r: filters.range, g: filters.groups, c: filters.cameras,
    s: filters.subject, sm: filters.subjectModels, m: filters.otherModels,
  }), [filters.time, filters.range, filters.groups, filters.cameras, filters.subject,
    filters.subjectModels, filters.otherModels]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // Forced to the plain list: the facet endpoint takes the same DSL, and a `place_by` or
        // `split_by` meant for another aggregate would be rejected outright.
        const scopeOnly: Filters = {
          ...filters, question: 'list', attributes: {}, known: null, personName: '', plate: '', splitBy: null,
          text: '', excludedTerms: [],
        };
        const f = await search.facets(toDsl(scopeOnly, subjectDef, 1, 0, dslOpts));
        if (live) setFacets(f || {});
      } catch {
        // A failed facet load must not block searching — the controls fall back to the full
        // vocabulary, and an unmatched value still returns a clear message from the server.
        if (live) setFacets({});
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, nodeKey, scopeKey]);

  /**
   * The event rows currently on screen — what the modal's ← / → step through.
   *
   * Derived rather than captured when a card is clicked, because the two answers that show cards
   * get them from different places: the plain list reads `result.rows`, while the Yes/No answer's
   * evidence comes from its own separate fetch. Deriving keeps "next" pointing at the same set
   * the operator is looking at.
   */
  const detailRows = useMemo<SearchEventRow[]>(() => {
    if (filters.question === 'verdict') return evidenceRows;
    return (result?.rows || []) as SearchEventRow[];
  }, [filters.question, evidenceRows, result]);

  /**
   * The playback window for a row, as ISO strings.
   *
   * Returns null when there is no usable timestamp — a row that cannot say WHEN it happened
   * cannot be played, and offering the control would be offering a dead button.
   */
  const playbackWindow = useCallback((row: SearchEventRow | null) => {
    if (!row?.event_timestamp || !row?.camera_id) return null;
    const at = new Date(row.event_timestamp).getTime();
    if (!Number.isFinite(at)) return null;
    return {
      from: new Date(at - PLAYBACK_LEAD_SEC * 1000).toISOString(),
      to: new Date(at + PLAYBACK_TAIL_SEC * 1000).toISOString(),
    };
  }, []);

  /**
   * The question can be un-answerable with the filters as they stand — a Yes/No with nowhere to
   * check, a compliance ratio with nothing to measure. The panel says what is missing; running
   * the query anyway would spend a round trip to produce something that cannot be shown.
   */
  const answerable = !(
    (questionSpec.needsPlace && !filtersHavePlace(filters))
    || (questionSpec.needsSplit && !filters.splitBy)
  );

  const runSearch = useCallback(async () => {
    // A new query invalidates the open record's POSITION — index 3 of the old answer is a
    // different event in the new one — so the modal closes rather than silently re-pointing.
    setDetailIndex(null);
    if (!answerable) { setResult(null); setResultQuestion(null); setError(null); return; }
    setLoading(true);
    setError(null);
    // Captured BEFORE the await: by the time it resolves the operator may have moved on, and
    // the answer has to be labelled with the question it actually answers.
    const asked = filters.question;
    try {
      const answer = await search.events(dsl);
      setResult(answer);
      setResultQuestion(asked);
    } catch (err: any) {
      // The server's message names the offending field ("crimson is not collected for
      // upper_color"). Surfacing it verbatim is the whole point of validating strictly.
      setError(err?.message || 'Search failed');
      setResult(null);
      setResultQuestion(null);
    } finally {
      setLoading(false);
    }
  }, [search, dsl, answerable, filters.question]);

  useEffect(() => { runSearch(); /* the filters ARE the query */ }, [runSearch]);

  /**
   * When a search FOR SOMEBODY comes back empty, is the window the reason?
   *
   * THIS IS THE DIFFERENCE BETWEEN "NOT HERE" AND "NOT TODAY", and getting it wrong is how the
   * whole feature reads as broken. The page opens on today. Type a colleague's name, and if they
   * happen not to have walked past a camera since midnight the answer is a blank screen — which
   * looks exactly like a name filter that does not work, and was reported as one. The registry
   * knows them, the cameras recognised them last week, and nothing on screen said so.
   *
   * So on an empty identity search the same query is re-run over thirty days, and what it finds
   * is offered as one sentence and one button. Bounded on purpose: only when the answer is empty,
   * only when a name or a plate is being searched (a blank result for "red shirt today" is an
   * ordinary answer, not a puzzle), and never when the window is already that wide or wider.
   *
   * Failure is silent. This is a hint about an empty answer; it must not become an error on top
   * of one.
   */
  const [widerWindow, setWiderWindow] = useState<{ events: number } | null>(null);

  useEffect(() => {
    setWiderWindow(null);
    if (loading || error || !result || (result.events ?? 0) > 0) return undefined;
    if (!identityFiltered(filters, subjectDef)) return undefined;
    // Already looking at a month or more — there is no wider window to offer.
    if (['last_30_days', 'this_month', 'last_month'].includes(filters.time.preset || '')) return undefined;

    let live = true;
    (async () => {
      try {
        const probe = toDsl(
          { ...filters, question: 'list', range: null, time: { preset: 'last_30_days' } },
          subjectDef, 1, 0, dslOpts,
        );
        probe.aggregate = 'count';
        const r = await search.events(probe);
        if (live && (r.events ?? 0) > 0) setWiderWindow({ events: r.events as number });
      } catch {
        // A hint that cannot be fetched is simply not shown.
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, nodeKey, result, loading, error, subjectDef]);

  /** The Yes/No answer's frames — see `evidenceRows`. Failure is silent: the verdict still holds. */
  useEffect(() => {
    if (filters.question !== 'verdict' || !answerable) { setEvidenceRows([]); return undefined; }
    let live = true;
    (async () => {
      try {
        const r = await search.events(toDsl({ ...filters, question: 'list' }, subjectDef, 12, 0, dslOpts));
        if (live) setEvidenceRows((r.rows || []) as SearchEventRow[]);
      } catch {
        // The verdict came from its own query and is unaffected. Showing it without the frames is
        // a worse answer than showing it with them, and a far better one than showing neither.
        if (live) setEvidenceRows([]);
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dsl, answerable, filters.question]);

  /**
   * Apply a typed sentence to the controls.
   *
   * `EMPTY_FILTERS` as the base, not the current filters: a new sentence is a new question, and
   * silently intersecting it with whatever was left selected from the last one would answer
   * something the operator did not type. The subject is the exception — it is kept unless the
   * sentence names another, because that is the tab they are working in.
   */
  const applyParse = useCallback((text: string) => {
    const p = parseQuery(text, { schema, cameras, groups, people, subject: filters.subject });
    setPage(0);
    /**
     * The sentence decides the MODE now, rather than being coerced into the page's own.
     *
     * "trail of the red shirt person" typed while Search is showing does not need a notice and
     * a second page any more — it flips the toggle, which is visible, reversible and exactly
     * what the operator asked for. `list` names no mode and leaves it alone.
     */
    const asked = p.filters?.question;
    const wantMode = (asked && MODE_OF_QUESTION[asked]) || mode;
    if (wantMode !== mode) { setMode(wantMode); onModeChange?.(wantMode); }
    /**
     * What THIS sentence says — a layer over the filters, not a replacement for them. See
     * `withSentence`: a camera picked in the panel, or a window chosen by hand, survives the
     * sentence being edited; only what the previous sentence itself contributed is replaced.
     */
    const layer: Partial<Filters> = { ...p.filters, question: drawableQuestion(asked) };
    /**
     * WORDS THE PARSER COULD NOT BIND ARE SEARCHED, NOT DROPPED — where the node can.
     *
     * The parser recognises a closed world: vocabulary, places, the people registry. A name that
     * is not registered ("badal"), a word painted on a wall — those go to `text`, matched
     * spelling-tolerantly against names, plates and OCR text, and the spellings used come back
     * on the bar for the operator to check. On an older node they stay amber, because that node
     * has nothing to search them with.
     *
     * On All the parser's own name and plate join the text too: that page has no name box and
     * no plate box, only words.
     */
    let shown = p;
    /**
     * Appearance has no meaning across every model at once — "red" on All is a person's top or
     * a car's paint. The merged page simply moves to the subject the attribute belongs to (the
     * switch is visible and one click back); a page LOCKED to All says so through the notice.
     */
    const subjectNow = () => lockedSubject || layer.subject || filters.subject;
    if (subjectNow() === 'all') {
      const attrs = Object.keys(layer.attributes || {}).filter((f) => layer.attributes?.[f]?.length);
      if (attrs.length) {
        const vehicleField = attrs.some((f) => (SUBJECT_FALLBACK.vehicle.fields as string[]).includes(f));
        const wanted: SubjectKey = vehicleField ? 'vehicle' : 'person';
        if (lockedSubject) shown = { ...p, filters: { ...p.filters, subject: wanted } };
        else layer.subject = wanted;
      }
    }
    if (textSearch) {
      const words = [...p.unparsed];
      if (subjectNow() === 'all') {
        if (layer.plate) words.unshift(layer.plate);
        if (layer.personName) words.unshift(layer.personName);
        delete layer.personName;
        delete layer.plate;
      }
      if (words.length) {
        layer.text = words.join(' ');
        shown = {
          ...shown,
          unparsed: [],
          notes: [...shown.notes, `Searching names, plates and text for “${layer.text}” — close spellings included`],
        };
      }
    }
    if (lockedSubject) {
      /**
       * The subject is the ROUTE here, so a sentence cannot change it. The identity box of the
       * other subject goes with it: `toDsl` only sends a plate for vehicles and a name for
       * people, so one left behind would be a chip the query never applies.
       */
      delete layer.subject;
      if (subjectDef.textFilter === 'plate') { delete layer.personName; delete layer.known; }
      else delete layer.plate;
    }
    const previous = sentenceLayer.current;
    sentenceLayer.current = layer;
    setFilters((prev) => {
      let base = withoutSentence(prev, previous, emptyFilters);
      if (layer.subject && layer.subject !== base.subject) base = convertSubject(base, layer.subject, subjects);
      return withSentence(base, layer);
    });
    setParsed(shown);
    setQueryText(text);
    return shown;
  }, [schema, cameras, groups, people, filters.subject, emptyFilters, mode,
    onModeChange, lockedSubject, subjectDef.textFilter, textSearch, subjects]);

  if (emptyState) return <>{emptyState}</>;

  /**
   * The result's rows, deliberately UNTYPED here.
   *
   * What they are depends on the aggregate — event rows for the list, places for "which areas"
   * and the verdict, trails for a journey, subjects for a compliance split. Each answer narrows
   * them itself; a blanket `as SearchEventRow[]` at this level would be a claim that is false for
   * four of the six questions, and it is what let place rows reach the card grid once already.
   * Only `.length` is read here, for the pager.
   */
  const rows = (result?.rows || []) as unknown[];
  const total = result?.events ?? 0;
  const distinct = result?.distinct_persons;
  /**
   * The listings this page may offer — a person is blacklisted, wanted or missing; a vehicle is
   * blacklisted or stolen. Taken from the watch-list screens' own vocabulary so the two never
   * drift, and narrowed to THIS page's subject: offering "stolen" on the Person page would be a
   * filter that can only ever return nothing.
   */
  const watchlistCategoryOptions = watchlistOptionsFor(filters.subject);

  const nActive = activeCount(filters, subjectDef);

  /**
   * THE SEARCH PANEL FOLDS AWAY.
   *
   * The query bar and two rows of filters are how an answer gets built, and dead weight once it
   * has been: on a laptop they cost about a third of the page, every pixel of which the ANSWER
   * wanted. An operator who has found the right query now spends the session scrolling a list
   * through a letterbox under controls they are not touching.
   *
   * What stays when it folds is the part that is not a control: the active-filter chips. A
   * folded panel that also hid what is narrowing the answer would turn "why are there only four
   * results" into a hunt, which is the failure mode of every collapsed filter bar that hides its
   * own state. Remembered per browser, because it is a working preference, not a mode.
   */
  const [panelOpen, setPanelOpen] = useState(() => {
    try { return localStorage.getItem('trinetra.search.panel') !== '0'; } catch { return true; }
  });
  const togglePanel = () => setPanelOpen((o) => {
    try { localStorage.setItem('trinetra.search.panel', o ? '0' : '1'); } catch { /* private mode */ }
    return !o;
  });

  /**
   * TRACK'S TWO RIGHT-HAND PANES — the path in the middle, the results on the right.
   *
   * Each folds to a rail, and never both at once: folding one while the other is folded
   * unfolds the other, because a page with two rails and nothing between them answers nothing.
   * Whichever is open alone takes the whole width.
   */
  const readFlag = (key: string) => {
    try { return localStorage.getItem(key) !== '0'; } catch { return true; }
  };
  const writeFlag = (key: string, on: boolean) => {
    try { localStorage.setItem(key, on ? '1' : '0'); } catch { /* private mode */ }
  };
  const [mapOpen, setMapOpen] = useState(() => readFlag('trinetra.search.trackMap'));
  const [resultsOpen, setResultsOpen] = useState(() => readFlag('trinetra.search.trackResults'));
  const toggleMap = () => {
    const next = !mapOpen;
    setMapOpen(next); writeFlag('trinetra.search.trackMap', next);
    if (!next && !resultsOpen) { setResultsOpen(true); writeFlag('trinetra.search.trackResults', true); }
  };
  const toggleResults = () => {
    const next = !resultsOpen;
    setResultsOpen(next); writeFlag('trinetra.search.trackResults', next);
    if (!next && !mapOpen) { setMapOpen(true); writeFlag('trinetra.search.trackMap', true); }
  };

  /**
   * Track result cards per row: 0 = auto (sized to the height), else 1–4 sharing the width.
   * A count the column cannot hold steps down by itself, so folding or unfolding the map
   * re-flows the cards rather than squeezing them — see ResultTimeline's `fixedTrack`.
   */
  const [trackPerRow, setTrackPerRow] = useState<number>(() => {
    try {
      const n = Number(localStorage.getItem('trinetra.search.trackPerRow'));
      return Number.isInteger(n) && n >= 0 && n <= 4 ? n : 0;
    } catch { return 0; }
  });
  const chooseTrackPerRow = (n: number) => {
    setTrackPerRow(n);
    try { localStorage.setItem('trinetra.search.trackPerRow', String(n)); } catch { /* private mode */ }
  };

  const [trackWidth, setTrackWidth] = useState(() => {
    try {
      const raw = Number(localStorage.getItem('trinetra.search.trackWidth'));
      if (Number.isFinite(raw) && raw > 0) return clampTrackWidth(raw);
    } catch { /* nothing remembered */ }
    return defaultTrackWidth();
  });
  const trackBodyRef = useRef<HTMLDivElement>(null);
  const trackDrag = useRef<{ x: number; w: number } | null>(null);
  const [trackDragging, setTrackDragging] = useState(false);
  /** The room the two panes share — the body row less the filter column beside them. */
  const trackRoom = () => {
    const row = trackBodyRef.current;
    const filters = row?.firstElementChild as HTMLElement | null;
    return row ? row.clientWidth - (filters?.offsetWidth || 0) : window.innerWidth;
  };
  const storeTrackWidth = (w: number) => {
    try { localStorage.setItem('trinetra.search.trackWidth', String(w)); } catch { /* private mode */ }
  };
  // A window that shrinks under a remembered width gives the map its floor back.
  useEffect(() => {
    const onResize = () => setTrackWidth((w) => clampTrackWidth(w, trackRoom()));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const attributeFields = subjectDef.fields;
  /** The every-model page: no attribute row, no "Detected by", one Types filter and the text. */
  const isAll = filters.subject === 'all';
  const activeModels = modelsFor(filters, subjectDef);
  /**
   * The models a name (or "enrolled only") added to the defaults, and the case where the
   * operator has ruled them all out by hand.
   *
   * Only face events carry an identity, so searching for a person by name against the default
   * person-detection rows can only ever answer "nobody". The query model unions face in; these
   * two say so on screen, because a page that searches more than its own control claims is as
   * misleading as one that silently searches less.
   */
  const identityAdded = identityAddedModels(filters, subjectDef);
  const identityDead = identityUnsearchable(filters, subjectDef);
  /** What the empty-window hint calls the thing being looked for. */
  const searchedNoun = subjectDef.textFilter === 'plate' && filters.plate.trim()
    ? `for ${filters.plate.trim().toUpperCase()}`
    : filters.personName.trim()
      ? `for ${filters.personName.trim()}`
      : filters.text.trim()
        ? `for “${filters.text.trim()}”`
        : 'matching';

  /** The spellings the answer matched on — highlighted on every card. */
  const spellings = useMemo(() => activeSpellings(result?.text_matches), [result]);

  /**
   * A name from the registry, for the query bar's "try this" row.
   *
   * The FIRST name the parser would actually match: a single word that collides with the
   * vocabulary is not usable as a typed name, and offering one as an example would hand the
   * operator a button that reads their colleague's name as a colour. One word, so the example
   * stays a short sentence.
   */
  const sampleName = useMemo(() => firstTypeableName(people, schema), [people, schema]);

  /**
   * The attribute filter that had to be set aside for a compliance question.
   *
   * Filtering to helmets and then measuring helmets is always 100%; the server rejects it, and
   * `toDsl` drops the filter so the rest of the query still runs. The one thing not allowed is
   * doing that silently, so the name comes back here for the panel to report.
   */
  const setAsideFilter = filters.question === 'compliance' && filters.splitBy
    && (filters.attributes[filters.splitBy.field] || []).includes(filters.splitBy.value)
    ? (FIELD_LABEL[filters.splitBy.field] || titleCase(filters.splitBy.field))
    : null;

  // ── Summaries for the closed dropdown buttons ──
  const timeSummary = filters.time.from && filters.time.to
    ? `${filters.time.from} → ${filters.time.to}`
    : (TIME_PRESETS.find((p) => p.value === filters.time.preset)?.label || 'Today');
  /**
   * The dragged range, in words. Shows the DATE too once the window spans more than a day —
   * "09:00 – 10:00" is ambiguous across a thirty-day window, and the chip is the only place the
   * narrowing is visible without looking at the chart.
   */
  const rangeSummary = (() => {
    if (!filters.range) return '';
    const from = new Date(filters.range.from);
    const to = new Date(filters.range.to);
    const sameDay = from.toDateString() === to.toDateString();
    const t = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    const d = (x: Date) => x.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return sameDay ? `${d(from)} ${t(from)} – ${t(to)}` : `${d(from)} ${t(from)} – ${d(to)} ${t(to)}`;
  })();

  const listSummary = (ids: string[], names: Map<string, string>, all = 'All') =>
    ids.length === 0 ? all
      : ids.length === 1 ? (names.get(ids[0]) || ids[0].slice(0, 8))
        : `${ids.length} selected`;

  /** The chosen place in words, for the Yes/No headline. */
  const placeLabel = (() => {
    const names = [
      ...filters.groups.map((id) => groupNames.get(id) || id.slice(0, 8)),
      ...filters.cameras.map((id) => cameraNames.get(id) || id.slice(0, 8)),
    ];
    if (names.length === 0) return 'anywhere';
    if (names.length <= 2) return names.join(' or ');
    return `${names.length} places`;
  })();

  /**
   * What is being searched for, as a phrase — "a person in a red top carrying a helmet".
   *
   * Built from the filters rather than the typed sentence, so it describes the query that ran
   * even when the operator adjusted the chips afterwards.
   */
  const subjectPhrase = (() => {
    const bits: string[] = [];
    const attr = (field: string) => filters.attributes[field] || [];
    if (attr('upper_color').length) bits.push(`a ${attr('upper_color').join(' or ')} top`);
    if (attr('lower_color').length) bits.push(`${attr('lower_color').join(' or ')} bottoms`);
    if (attr('gender').length) bits.push(attr('gender').join(' or '));
    if (attr('accessories').length) bits.push(`carrying a ${attr('accessories').join(' or ')}`);
    if (attr('vehicle_type').length) bits.push(attr('vehicle_type').join(' or '));
    if (filters.personName.trim()) bits.push(`named ${filters.personName.trim()}`);
    if (filters.plate.trim()) bits.push(`plate ${filters.plate.trim().toUpperCase()}`);
    if (filters.text.trim()) bits.push(`“${filters.text.trim()}”`);
    const noun = filters.subject === 'vehicle' ? 'a vehicle'
      : filters.subject === 'all' ? 'anything'
        : filters.known ? 'an enrolled person' : 'a person';
    return bits.length ? `${noun} with ${bits.join(', ')}` : noun;
  })();

  /** Every attribute value, per field, for the "what to measure" control. */
  const splitOptions: FilterOption[] = attributeFields.flatMap((field) => (
    (schema?.attributes?.[field] || [])
      .filter((v) => v !== 'unknown')
      .map<FilterOption>((v) => ({
        value: `${field}:${v}`,
        label: titleCase(v),
        group: FIELD_LABEL[field] || titleCase(field),
        count: (facets[field] || []).find((f) => f.value === v)?.events ?? 0,
      }))
  ));

  /**
   * The card grid, over the rows it is GIVEN.
   *
   * Takes its rows as an argument rather than closing over `result.rows`, because what those rows
   * are depends on the aggregate: event rows for the plain list, but places for the verdict,
   * trails for a journey and subjects for a compliance split. Passing them in is what makes it
   * impossible to render a place row as an event card.
   */
  const cardGrid = (eventRows: SearchEventRow[]) => (
    <EventListCards
      events={eventRows}
      columns={resultColumns(subjectDef, spellings)}
      /* The default is four, which "fills two tidy rows" for a report card. This one is the
         Search answer and every attribute is a tile, so four would silently drop the last
         three of them — a card that looks complete and is not. Identity + the subject's
         fields + the model, with the camera already over the frame. */
      factLimit={subjectDef.fields.length + 2 + (showsReadText(subjectDef) ? 1 : 0)}
      /* Cards sized to the room the answer actually has — which now changes under the operator,
         since the panel above folds and the dock below is dragged: the cards shed facts,
         padding and width — in that order — rather than the page shedding rows. See
         `fitCardLayout`. */
      fitToHeight
      loading={loading}
      context={{ cameraName: (id) => cameraNames.get(id) }}
      imageFor={imageFor ? (e) => imageFor(e as SearchEventRow) : undefined}
      loadImage={loadImage ? (e) => loadImage(e as SearchEventRow) : undefined}
      // The modal wins when the host supplied one. `onOpenEvent` is not dropped — it becomes the
      // modal's "Full page" button, so the routed record stays reachable for links and bookmarks.
      onOpen={(index) => {
        if (eventDetail) setDetailIndex(index);
        else onOpenEvent?.(eventRows[index]);
      }}
      cardAction={playback ? (event: any) => {
        // Offered whenever the row has a usable timestamp. A camera with no recording
        // still gets the button — the panel then says WHY (retention, transcode, recording
        // off), which is more useful than a control that silently is not there.
        if (!playbackWindow(event as SearchEventRow)) return null;
        return (
          <button
            type="button"
            onClick={() => setPlaybackRow(event as SearchEventRow)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-foreground hover:bg-muted"
            title="Play the recording around this moment"
          >
            <PlayCircleIcon className="h-3.5 w-3.5" />
            Playback
          </button>
        );
      } : undefined}
      emptyTitle={result?.camera_scope === 0 ? 'No cameras in scope' : 'Nothing matched'}
      emptyHint={
        result?.camera_scope === 0
          ? 'You do not have access to any camera matching these filters.'
          : 'Try widening the time window, or removing an appearance filter.'
      }
    />
  );

  /** Narrow the query to one place — how a "which areas" row becomes the next search. */
  const pickPlace = (place: string, placeBy: SearchPlaceBy) => {
    setPage(0);
    setParsed(null);
    setFilters((prev) => (placeBy === 'group'
      ? { ...prev, groups: [...new Set([...prev.groups, place])], question: 'list' }
      : { ...prev, cameras: [...new Set([...prev.cameras, place])], question: 'list' }));
  };

  function renderAnswer() {
    // `staleShape` here as well as at the call site, so the invariant travels with the function:
    // below this line `result` is an answer TO `filters.question`, and every `case` may read
    // its own row shape off it without checking.
    if (!answerable || !result || staleShape) {
      // The two un-answerable states own their own explanation, because what is missing differs.
      if (filters.question === 'verdict') {
        return (
          <VerdictAnswer
            result={result || { success: true }}
            namePlace={namePlace}
            placeLabel={placeLabel}
            hasPlace={filtersHavePlace(filters)}
            subjectPhrase={subjectPhrase}
          />
        );
      }
      if (filters.question === 'compliance') {
        return (
          <ComplianceAnswer
            result={result || { success: true }}
            knownOnly={filters.known === true}
            setAsideFilter={setAsideFilter}
          />
        );
      }
      return null;
    }

    switch (filters.question) {
      case 'when':
        return <WhenAnswer result={result} />;
      case 'where':
        // The frame resolvers go in too: "which places" is the answer a plate search lands on,
        // and a row of counts without the picture leaves the operator to open each camera to
        // confirm it is the vehicle they are after. Camera rows only — see WhereAnswer.
        return (
          <WhereAnswer
            result={result}
            namePlace={namePlace}
            groups={groups}
            onPickPlace={pickPlace}
            imageFor={imageFor as any}
            loadImage={loadImage as any}
          />
        );
      case 'verdict':
        return (
          <VerdictAnswer
            result={result}
            namePlace={namePlace}
            placeLabel={placeLabel}
            hasPlace
            subjectPhrase={subjectPhrase}
            evidence={evidenceRows.length > 0 ? (
              <div className="min-h-0">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  The evidence
                </div>
                {cardGrid(evidenceRows)}
              </div>
            ) : undefined}
          />
        );
      case 'compliance':
        return (
          <ComplianceAnswer
            result={result}
            knownOnly={filters.known === true}
            setAsideFilter={setAsideFilter}
          />
        );
      case 'list':
      default: {
        /**
         * SEARCH draws the detections; TRACK folds them into stops.
         *
         * This was a remembered layout toggle, and it was the mode in disguise — see the note
         * on `TrailOrder`. The folded view collapses forty detections at one camera into one
         * row, which is the right answer to "where did they go" and throws away the
         * per-detection appearance that Search exists to show.
         */
        if (mode === 'search') return cardGrid(rows as SearchEventRow[]);
        const eventRows = rows as SearchEventRow[];
        return (
          <ResultTimeline
            rows={eventRows}
            loading={loading}
            order={trailOrder}
            cameraName={(id) => cameraNames.get(id)}
            imageFor={imageFor}
            loadImage={loadImage}
            // The SAME index the grid hands over, into the SAME rows, so the record modal and its
            // ← / → pager behave identically whichever layout found the hit.
            onOpen={(index) => {
              if (eventDetail) setDetailIndex(index);
              else onOpenEvent?.(eventRows[index]);
            }}
            onPlay={playback ? (row) => setPlaybackRow(row) : undefined}
            playable={(row) => !!playbackWindow(row)}
            cameraScope={result?.camera_scope}
            total={total}
            perRow={trackPerRow}
          />
        );
      }
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* ── Header ── */}
      <div className="flex shrink-0 items-center gap-2 border-b border-border bg-card px-4 py-2.5">
        {/* The icon follows the MODE, so the header changes visibly when the toggle moves —
            the title alone is two words that differ by one. */}
        {mode === 'tracking'
          ? <MapPinIcon className="h-5 w-5 text-muted-foreground" />
          : <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />}
        <h1 className="text-lg font-semibold text-foreground">{lockedSubject ? chrome.title : 'Search & Track'}</h1>

        {/*
          ── SEARCH or TRACK, on the same query ──

          The one control that used to be a second page. Everything below it is identical
          either way — same filters, same vocabulary, same fetch — so flipping it re-reads the
          answer rather than starting again, and an operator who built a query to FIND somebody
          and then wants to FOLLOW them does not rebuild it.

          A segmented radiogroup rather than tabs: it selects the answer SHAPE for one page, it
          does not navigate between two, and announcing tabs would promise arrow-key movement
          between panels that do not exist.

          `bg-brand text-white` for the selected rung — `bg-primary` resolves to nothing on
          either dashboard, which is the trap that made three other selected states here
          invisible.
        */}
        <div
          className="flex overflow-hidden rounded-md border border-border"
          role="radiogroup"
          aria-label="What to do with the results"
        >
          {([
            { key: 'search' as SearchPageMode, label: 'Search', Icon: MagnifyingGlassIcon,
              hint: 'Every matching detection as a card, with all of its attributes' },
            { key: 'tracking' as SearchPageMode, label: 'Track', Icon: MapPinIcon,
              hint: 'The same matches as a trail — one stop per camera visit, in order' },
          ]).map(({ key, label, Icon, hint }) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={mode === key}
              onClick={() => chooseMode(key)}
              title={hint}
              className={[
                'flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium transition-colors',
                mode === key ? 'bg-brand text-white' : 'text-foreground hover:bg-muted',
              ].join(' ')}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        <span className="hidden text-xs text-muted-foreground lg:inline">
          {lockedSubject ? chrome.tagline : SUBJECT_CHROME[mode][filters.subject].tagline}
        </span>

        {/* The layout, readable and changeable in one place: which panes are showing. */}
        <div className="ml-auto">
          <PaneToggles
            panes={[
              { side: 'left' as const, label: 'Filters', on: panelOpen, onToggle: togglePanel },
              ...(mode === 'tracking' ? [
                { side: 'center' as const, label: 'Map', on: mapOpen, onToggle: toggleMap },
                { side: 'right' as const, label: 'Results', on: resultsOpen, onToggle: toggleResults },
              ] : []),
            ]}
          />
        </div>

        <button
          type="button"
          onClick={runSearch}
          className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs text-foreground hover:bg-muted"
          title="Run the search again"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/*
        ── BODY: SEARCH ON THE LEFT, THE ANSWER ON THE RIGHT ──

        The query bar and the filters are a column beside the answer rather than a band above
        it. Stacked on top they took a third of the page's HEIGHT, which is exactly what a list
        of cards and the tracking path under it are short of; a column takes width instead, and
        a results page has width to spare. The right-hand side is split horizontally — the
        answer above, the tracking path docked below it — so on Track both read together.

        The column collapses to a thin rail (header button, or the rail's own button), and the
        active-filter chips live on the RIGHT, above the answer, so collapsing the controls
        never hides what is narrowing the results. See `panelOpen`.
      */}
      <div ref={trackBodyRef} className="flex min-h-0 flex-1">
      {/*
        Collapses like the app's side navigation: the width animates, a round button on the
        right edge folds and unfolds it, and folded it is a narrow rail. The controls stay
        MOUNTED while folded (just hidden), so a half-typed sentence or an open choice survives
        a fold — unmounting them threw that away.
      */}
      <div
        className={`relative shrink-0 border-r border-border bg-card transition-[width] duration-300 ease-in-out ${
          panelOpen ? 'w-[18rem] xl:w-[19.5rem]' : 'w-11'
        }`}
      >
      <div className="h-full overflow-hidden">
      <aside
        id="search-panel"
        aria-label="Search"
        aria-hidden={!panelOpen}
        className={`flex h-full w-[18rem] flex-col overflow-y-auto transition-opacity duration-200 xl:w-[19.5rem] ${
          panelOpen ? 'opacity-100' : 'invisible opacity-0'
        }`}
      >
      {/* The panel's own title and its Hide — the same control every pane on this page has. */}
      <div className="sticky top-0 z-20 flex shrink-0 items-center justify-between border-b border-border bg-card px-3 py-1.5">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
          Search filters
        </span>
        <HidePaneButton side="left" label="search filters" onClick={togglePanel} />
      </div>

      {/* ── The text bar. Sets the controls below; never queries on its own. ── */}
      <QueryBar
        onSubmit={applyParse}
        parsed={parsed}
        nameExample={nameExampleFor(mode, lockedSubject || filters.subject, sampleName)}
        initialText={initialQuery}
        /* Held until the vocabulary has arrived. The parser resolves camera names, groups and
           attribute values against it, so a sentence applied before it loads would silently
           drop half of itself — and with apply-as-you-type nobody presses a button afterwards
           to make it right. */
        ready={!!schema}
        examples={examplesFor(mode, lockedSubject || filters.subject)}
        placeholder={mode === 'tracking'
          ? 'Ask in words — “where did the red shirt person go”'
          : undefined}
        onClear={() => {
          // Emptying the box takes back what the SENTENCE set — and nothing picked by hand.
          // "Clear all" in the panel is the control that resets everything.
          const previous = sentenceLayer.current;
          sentenceLayer.current = null;
          setParsed(null); setPage(0); setQueryText('');
          setFilters((prev) => withoutSentence(prev, previous, emptyFilters));
        }}
      />

      {/*
        The sentence is about the OTHER subject — a plate typed on the Person page.

        This is the only miss left. A sentence asking for a trail no longer earns a notice: it
        flips the Search/Track toggle above, because both readings live here now. A subject
        cannot be flipped the same way — the two share no attribute and no identity field, and
        the filter bar is built out of one of them — so it is said out loud, with a button. The
        alternative is answering a question nobody asked and presenting it as the answer.
      */}
      {askedOtherSubject && (
        <SwitchNotice
          message={`That reads as a ${askedOtherSubject} — this page is about ${subjectDef.label.toLowerCase()}s. The rest of your filters were kept.`}
          action={onSwitch && {
            label: SUBJECT_PAGE_NAME[askedOtherSubject],
            onClick: () => onSwitch({ subject: askedOtherSubject, query: queryText }),
          }}
        />
      )}

      {/*
        ── THE FILTER PANEL, as labelled sections ──

        It was two rows of inline pills ("Scope", "Looking for") wrapping into a ragged block in
        a 17rem column, where the identity boxes were hidden two clicks deep inside dropdowns.
        Now each dimension is a full-width row under a heading that says what it narrows, the
        time presets are one click, and the name / plate / text boxes are typed into directly.
      */}
      <div className="shrink-0 space-y-3 px-3 py-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-foreground">Filters</span>
          {nActive > 0 && (
            <button
              type="button"
              // Keeps the SUBJECT and the QUESTION: "clear all" means clear the filters, not
              // change what is being asked.
              onClick={() => {
                setPage(0);
                setParsed(null);
                sentenceLayer.current = null;
                setFilters({ ...emptyFilters, subject: filters.subject, question: filters.question });
              }}
              className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              Clear all ({nActive})
            </button>
          )}
        </div>

        {/* ── LOOKING FOR — the three pages this used to be, as one switch. On a LOCKED mount the
              route is the subject, and a second way to change it would leave the title behind. ── */}
        {!lockedSubject && (
          <FilterSection title="Looking for">
            <div className="grid grid-cols-3 overflow-hidden rounded-md border border-border" role="radiogroup" aria-label="Looking for">
              {SUBJECT_ORDER.map((key) => {
                const isOn = filters.subject === key;
                const Icon = key === 'person' ? UserIcon : key === 'vehicle' ? TruckIcon : Squares2X2Icon;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={isOn}
                    onClick={() => chooseSubject(key)}
                    title={SUBJECT_HINT[key]}
                    className={[
                      'flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium transition-colors',
                      // `bg-primary` generates no CSS on either dashboard — brand is the token.
                      isOn ? 'bg-brand text-white' : 'text-foreground hover:bg-muted',
                    ].join(' ')}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {SUBJECT_PAGE_NAME[key]}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] leading-snug text-muted-foreground">{SUBJECT_HINT[filters.subject]}</p>
          </FilterSection>
        )}

        {/* ── WHEN — presets as one-click chips; an exact window behind "Custom". ── */}
        <FilterSection title="When">
          <div className="grid grid-cols-3 gap-1">
            {TIME_PRESETS.map((p) => {
              const on = !filters.time.from && (filters.time.preset || 'today') === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => { setCustomOpen(false); set('time', { preset: p.value }); }}
                  className={[
                    'truncate rounded-md border px-1.5 py-1 text-[11px] transition-colors',
                    on ? 'border-brand bg-brand-light font-medium text-foreground' : 'border-border text-foreground hover:bg-muted',
                  ].join(' ')}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setCustomOpen((o) => !o)}
            aria-expanded={customOpen}
            className={[
              'w-full rounded-md border px-2 py-1 text-left text-[11px] transition-colors',
              customOpen || filters.time.from ? 'border-brand bg-brand-light text-foreground' : 'border-border text-muted-foreground hover:bg-muted',
            ].join(' ')}
          >
            {filters.time.from && filters.time.to ? `Custom: ${timeSummary}` : 'Custom range…'}
          </button>
          {customOpen && (
            <div className="space-y-1">
              <label className="block text-[10px] text-muted-foreground">
                From
                <input
                  type="datetime-local"
                  value={filters.time.from || ''}
                  onChange={(e) => set('time', { ...filters.time, preset: undefined, from: e.target.value })}
                  className="mt-0.5 w-full rounded border border-border bg-background px-1.5 py-1 text-xs text-foreground"
                />
              </label>
              <label className="block text-[10px] text-muted-foreground">
                To
                <input
                  type="datetime-local"
                  value={filters.time.to || ''}
                  onChange={(e) => set('time', { ...filters.time, preset: undefined, to: e.target.value })}
                  className="mt-0.5 w-full rounded border border-border bg-background px-1.5 py-1 text-xs text-foreground"
                />
              </label>
              {filters.time.from && !filters.time.to && (
                <p className="text-[10px] text-amber-500">Set both ends to use a custom range.</p>
              )}
            </div>
          )}
        </FilterSection>

        {/* ── WHERE ── */}
        <FilterSection title="Where">
          <FilterDropdown
            variant="row"
            label="Groups"
            summary={listSummary(filters.groups, groupNames)}
            active={filters.groups.length > 0}
            disabled={groups.length === 0}
            disabledReason="No camera groups on this node"
            panelClassName="w-72"
          >
            {() => (
              <OptionList
                searchable={groups.length > 8}
                searchPlaceholder="Find a group…"
                hint="Includes sub-groups. Groups and cameras ADD together — pick both to search the group plus that camera."
                options={(groups as any[]).map((g) => ({ value: g.id, label: g.name }))}
                selected={filters.groups}
                onChange={(v) => set('groups', v)}
                emptyText="No matching group"
              />
            )}
          </FilterDropdown>

          <FilterDropdown
            variant="row"
            label="Cameras"
            summary={listSummary(filters.cameras, cameraNames)}
            active={filters.cameras.length > 0}
            disabled={cameras.length === 0}
            disabledReason="No cameras visible to you on this node"
            panelClassName="w-72"
          >
            {() => (
              <OptionList
                searchable
                searchPlaceholder="Find a camera…"
                options={cameras.map((c) => ({ value: c.id, label: c.name }))}
                selected={filters.cameras}
                onChange={(v) => set('cameras', v)}
                emptyText="No matching camera"
              />
            )}
          </FilterDropdown>

          {/* How the "which areas?" answer buckets its places — only that question uses it. */}
          {filters.question === 'where' && (
            <FilterDropdown
              variant="row"
              label="Group by"
              summary={PLACE_BY_OPTIONS.find((o) => o.value === filters.placeBy)?.label || 'Area'}
              active
              panelClassName="w-64"
            >
              {(close) => (
                <OptionList
                  mode="single"
                  hint="An AREA is a camera group, so areas nest and their rows overlap. Camera and zone rows do not."
                  options={PLACE_BY_OPTIONS.map<FilterOption>((o) => ({
                    value: o.value,
                    label: o.label,
                    disabledReason: o.hint,
                  }))}
                  selected={[filters.placeBy]}
                  onChange={([v]) => set('placeBy', v as SearchPlaceBy)}
                  close={close}
                />
              )}
            </FilterDropdown>
          )}
        </FilterSection>

        {/* ── EVENT TYPES — which models are searched. ── */}
        <FilterSection title="Event types">
          {/* "Detected by" only where the subject HAS a choice. */}
          {subjectDef.models.length > 1 && !isAll && (
            <FilterDropdown
              variant="row"
              label="Detected by"
              summary={activeModels.length === subjectDef.models.length
                ? 'All models'
                : activeModels.map((m) => MODEL_LABEL[m] || titleCase(m)).join(' + ')}
              active={activeModels.length !== subjectDef.defaultModels.length}
              panelClassName="w-72"
            >
              {() => (
                <OptionList
                  hint={identityAdded.length
                    ? `${identityAdded.map((m) => MODEL_LABEL[m] || titleCase(m)).join(' + ')} `
                      + 'is being searched because you are looking for a specific person — it is the only '
                      + 'model that records who someone is. Untick it and the name can no longer match anything.'
                    : 'Person events carry the clothing attributes; face events carry the matched identity. Selecting both can show one person twice in a list — the When, Which-areas and Compliance answers count distinct people and are not affected.'}
                  options={subjectDef.models.map<FilterOption>((m) => ({
                    value: m,
                    label: MODEL_LABEL[m] || titleCase(m),
                    count: undefined,
                    disabledReason: subjectDef.defaultModels.includes(m) ? 'Searched by default' : undefined,
                  }))}
                  selected={activeModels}
                  onChange={(v) => set('subjectModels', v)}
                  emptyText="Schema not loaded"
                />
              )}
            </FilterDropdown>
          )}

          {/* On All this is THE model filter (nothing picked = every model). On a subject it adds
              the models that belong to neither — fire, fall, intrusion, PPE… — beside it. */}
          <FilterDropdown
            variant="row"
            label={isAll ? 'Types' : 'Also include'}
            summary={filters.otherModels.length === 0 ? (isAll ? 'All types' : 'None')
              : filters.otherModels.length === 1
                ? (MODEL_LABEL[filters.otherModels[0]] || titleCase(filters.otherModels[0]))
                : `${filters.otherModels.length} selected`}
            active={filters.otherModels.length > 0}
            panelClassName="w-60"
          >
            {() => (
              <OptionList
                hint={isAll
                  ? 'Every model is searched until you pick some. Text matches a name on a face event, a plate on a vehicle or OCR event, and any word the OCR model read.'
                  : `Added ALONGSIDE ${subjectDef.label.toLowerCase()} events. These models carry no attributes and no subject, so the ${subjectDef.label.toLowerCase()} filters do not apply to them.`}
                options={(schema?.models || Object.keys(MODEL_LABEL))
                  .filter((m) => isAll || !SUBJECT_MODELS.has(m))
                  .map<FilterOption>((m) => ({
                    value: m,
                    label: MODEL_LABEL[m] || titleCase(m),
                    group: MODEL_GROUP[m] || 'Other',
                  }))}
                selected={filters.otherModels}
                onChange={(v) => set('otherModels', v)}
                emptyText="Schema not loaded"
              />
            )}
          </FilterDropdown>
        </FilterSection>

        {/* ── APPEARANCE — the subject's own attributes. None on All: a colour means nothing
              across every model at once. ── */}
        {(attributeFields.length > 0 || filters.question === 'compliance') && (
          <FilterSection title="Appearance">
            {attributeFields.map((field) => {
              const available = facets[field];
              // Facets loaded and empty → nothing of this kind has been recorded in the current
              // scope. Reported as a NOTICE inside the panel, not as a disabled control.
              const notCollected = Array.isArray(available) && available.length === 0;
              // ALWAYS the full vocabulary from the schema; facets supply the COUNTS only.
              const values = schema?.attributes?.[field]
                || (available || []).map((f) => f.value);
              const counts = Object.fromEntries((available || []).map((f) => [f.value, f.events]));
              const chosen = filters.attributes[field] || [];
              const label = FIELD_LABEL[field] || titleCase(field);
              // Under a compliance question this field IS the measurement, so filtering on it too
              // would make the ratio 100%. Said on the control rather than discovered in the answer.
              const isSplitField = filters.question === 'compliance' && filters.splitBy?.field === field;
              const notice = isSplitField
                ? `This is what the compliance answer is measuring, so a filter here is ignored — every match would have a ${titleCase(filters.splitBy!.value).toLowerCase()}.`
                : notCollected ? NOT_COLLECTED_NOTICE : undefined;

              return (
                <FilterDropdown
                  key={field}
                  variant="row"
                  label={label}
                  summary={summariseValues(chosen)}
                  active={chosen.length > 0}
                  adornment={COLOR_FIELDS.has(field) && chosen.length > 0 ? (
                    <span className="flex -space-x-0.5">
                      {chosen.slice(0, 3).map((v) => (
                        <Swatch key={v} value={v} className="ring-card" />
                      ))}
                    </span>
                  ) : undefined}
                  panelClassName={COLOR_FIELDS.has(field) ? 'w-56' : 'w-52'}
                >
                  {() => (COLOR_FIELDS.has(field) ? (
                    <SwatchGrid
                      values={values}
                      selected={chosen}
                      counts={counts}
                      onChange={(v) => setAttribute(field, v)}
                      notice={notice}
                    />
                  ) : (
                    <OptionList
                      hint={notice}
                      options={values.map<FilterOption>((v) => ({
                        value: v,
                        label: titleCase(v),
                        // 0 rather than undefined, so the list dims what has no data in scope
                        // while still letting it be picked.
                        count: counts[v] ?? 0,
                      }))}
                      selected={chosen}
                      onChange={(v) => setAttribute(field, v)}
                      emptyText="Vocabulary not loaded"
                    />
                  ))}
                </FilterDropdown>
              );
            })}

            {/* What the compliance answer measures — it DIVIDES the matching people rather than
                narrowing them, which the panel's own hint spells out. */}
            {filters.question === 'compliance' && (
              <FilterDropdown
                variant="row"
                label="Had a"
                summary={filters.splitBy ? titleCase(filters.splitBy.value) : 'Pick one'}
                active={!!filters.splitBy}
                panelClassName="w-64"
              >
                {(close) => (
                  <OptionList
                    mode="single"
                    searchable={splitOptions.length > 12}
                    searchPlaceholder="Find a value…"
                    hint="Divides the matching PEOPLE into those who had it and those who did not. One sighting showing it is enough."
                    options={splitOptions}
                    selected={filters.splitBy ? [`${filters.splitBy.field}:${filters.splitBy.value}`] : []}
                    onChange={([v]) => {
                      const [field, ...rest] = v.split(':');
                      set('splitBy', { field, value: rest.join(':') });
                    }}
                    close={close}
                    emptyText="Vocabulary not loaded"
                  />
                )}
              </FilterDropdown>
            )}
          </FilterSection>
        )}

        {/* ── IDENTITY — typed straight into the panel. The name box for people, the plate box for
              vehicles; All has neither, its identity IS the text. ── */}
        <FilterSection title="Identity & text">
          {!isAll && subjectDef.textFilter === 'person' && (
            <>
              <TextFilter
                label="Name"
                value={filters.personName}
                onChange={(v) => set('personName', v)}
                placeholder="e.g. Asha"
                hint={`${textSearch ? 'Any word of the name, close spellings included.' : 'Matches from the start of the name.'} Employees and registered visitors alike.`}
              />
              {/* Said where the name is typed: person detection records no identity, so a name
                  can only be answered by face recognition, and the search adds it. */}
              {identityAdded.length > 0 && (
                <p className="text-[10px] leading-snug text-brand">
                  Searching {identityAdded.map((m) => MODEL_LABEL[m] || titleCase(m)).join(' + ')}
                  {' '}as well — only it records who a person is.
                </p>
              )}
              {identityDead && (
                <p className="text-[10px] leading-snug text-amber-500">
                  “Detected by” is set to models that record no identity, so this name cannot match
                  anything. Add {subjectDef.identityModels.map((m) => MODEL_LABEL[m] || titleCase(m)).join(' or ')}.
                </p>
              )}
              {/* "Enrolled" means the face matched a registered person — the closest the system
                  gets to a staff list. */}
              <Segmented
                label="Enrolment"
                options={[
                  { value: 'any', label: 'Anyone' },
                  { value: 'known', label: 'Enrolled' },
                  { value: 'unknown', label: 'Unidentified' },
                ]}
                value={filters.known === null ? 'any' : filters.known ? 'known' : 'unknown'}
                onChange={(v) => set('known', v === 'any' ? null : v === 'known')}
              />
            </>
          )}

          {!isAll && subjectDef.textFilter === 'plate' && (
            <TextFilter
              label="Plate"
              value={filters.plate}
              onChange={(v) => set('plate', v)}
              placeholder="MH12AB1234 or just MH12"
              uppercase
              hint={`From the start, so MH12 finds the series; spaces and dashes ignored.${textSearch ? ' A whole plate also finds close misreads, OCR reads included.' : ''}`}
            />
          )}

          {/* Free text, spelling-tolerant. The query bar fills it with any words it could not
              turn into a filter, so a sentence is never half-ignored. */}
          {textSearch && (
            <TextFilter
              label={isAll ? 'Name, plate or text' : 'Text'}
              value={filters.text}
              onChange={(v) => set('text', v)}
              placeholder={isAll ? 'badal, MH01HR4321, dethali…'
                : subjectDef.textFilter === 'plate' ? 'MH01HR4321' : 'badal'}
              hint={`Searches ${subjectDef.textFields.map((f) => (f === 'text' ? 'OCR text' : `${f}s`)).join(', ')}. Close spellings count — badal also finds vadal.`}
            />
          )}

          {/* The watch list is the one identity question both subjects share, and it answers
              against the list AS IT STANDS — sightings from before somebody was flagged count. */}
          <FilterDropdown
            variant="row"
            label="Watch list"
            summary={filters.watchlist === 'any' ? 'Any'
              : filters.watchlist === 'not_listed' ? 'Not listed'
                : filters.watchlistCategories.length
                  ? filters.watchlistCategories.map((c) => titleCase(c)).join(', ')
                  : 'Listed'}
            active={filters.watchlist !== 'any'}
            panelClassName="w-60"
          >
            {(close) => (
              <div>
                <OptionList
                  mode="single"
                  hint="Flagged people and vehicles, as the watch list stands now — so a sighting from before somebody was flagged is still found."
                  options={[
                    { value: 'any', label: 'Anyone' },
                    { value: 'listed', label: 'On a watch list' },
                    { value: 'not_listed', label: 'Not on any watch list' },
                  ]}
                  selected={[filters.watchlist]}
                  onChange={([v]) => {
                    setFilters((prev) => ({
                      ...prev,
                      watchlist: v as Filters['watchlist'],
                      // Categories narrow "listed" only; kept under the other two they would be
                      // an invisible filter, and the server refuses the pair.
                      watchlistCategories: v === 'listed' ? prev.watchlistCategories : [],
                    }));
                    setPage(0);
                    if (v !== 'listed') close();
                  }}
                />
                {filters.watchlist === 'listed' && (
                  <div className="border-t border-border">
                    <OptionList
                      mode="multi"
                      hint="Any listing, unless you narrow it."
                      options={watchlistCategoryOptions.map((c) => ({ value: c.value, label: c.label }))}
                      selected={filters.watchlistCategories}
                      onChange={(values) => { set('watchlistCategories', values); }}
                      close={close}
                    />
                  </div>
                )}
              </div>
            )}
          </FilterDropdown>
        </FilterSection>
      </div>
      </aside>
      </div>

        {/* The folded rail: a way back in, and the count of what is still narrowing the answer,
            so a folded panel never hides that the results are filtered. */}
        {!panelOpen && (
          <PaneRail
            label="Search filters"
            Icon={AdjustmentsHorizontalIcon}
            count={nActive}
            onExpand={togglePanel}
            opens="right"
            className="absolute inset-0"
          />
        )}
      </div>

      {/*
        ── TRACK: THE PATH IN THE MIDDLE ──

        Search reads as one answer beside its filters. Track reads as a route AND its stops, so
        the route gets the middle of the page at full height — a floor plan squeezed into a
        strip under a list was a map nobody could read — and the stops become a column on the
        right, beside the pins they number. The column is dragged wider or narrower on the rule
        between them, and either pane folds to a rail; see `mapOpen`.
      */}
      {mode === 'tracking' && (mapOpen ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <TrackingPathPanel
            fill
            onCollapse={toggleMap}
            stops={pathStops}
            nodeKey={nodeKey}
            // The same map the cards beside it use, so a pin names its camera identically.
            cameraName={(id) => cameraNames.get(id)}
          />
        </div>
      ) : (
        <PaneRail label="Tracking map" Icon={MapIcon} onExpand={toggleMap} opens="right" className="border-r border-border" />
      ))}

      {/* The rule between the map and the results: drag (or arrow keys) to trade width. */}
      {mode === 'tracking' && mapOpen && resultsOpen && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize track results"
          tabIndex={0}
          onPointerDown={(e) => {
            e.preventDefault();
            trackDrag.current = { x: e.clientX, w: trackWidth };
            setTrackDragging(true);
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            const from = trackDrag.current;
            if (!from) return;
            // Dragging LEFT widens the results: the rule is the column's left edge.
            setTrackWidth(clampTrackWidth(from.w + (from.x - e.clientX), trackRoom()));
          }}
          onPointerUp={() => {
            if (!trackDrag.current) return;
            trackDrag.current = null;
            setTrackDragging(false);
            setTrackWidth((w) => { storeTrackWidth(w); return w; });
          }}
          onPointerCancel={() => { trackDrag.current = null; setTrackDragging(false); }}
          onDoubleClick={() => { const w = defaultTrackWidth(); setTrackWidth(w); storeTrackWidth(w); }}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 96 : 24;
            const delta = e.key === 'ArrowLeft' ? step : e.key === 'ArrowRight' ? -step : 0;
            if (!delta) return;
            e.preventDefault();
            const w = clampTrackWidth(trackWidth + delta, trackRoom());
            setTrackWidth(w); storeTrackWidth(w);
          }}
          title="Drag to resize · double-click to reset"
          className={`group relative z-10 flex w-1.5 shrink-0 cursor-col-resize touch-none items-center justify-center border-l border-border outline-none ${
            trackDragging ? 'bg-brand-light' : 'hover:bg-muted focus-visible:bg-brand-light'
          }`}
        >
          <span
            className={`h-8 w-0.5 rounded-full transition-colors ${
              trackDragging ? 'bg-brand' : 'bg-border group-hover:bg-muted-foreground'
            }`}
          />
        </div>
      )}

      {mode === 'tracking' && !resultsOpen && (
        <PaneRail label="Track results" Icon={ListBulletIcon} count={total} onExpand={toggleResults} opens="left" className="border-l border-border" />
      )}

      {/* ── The right-hand side: what is selected, the status, the answer.

             On Track it is a column of its own width beside the map — or the whole width when
             the map is folded. Folded itself it is zero wide rather than unmounted, so the page
             it had scrolled to survives, and the record modal and playback dock (both `fixed`,
             so not clipped) keep working from a click on the map. ── */}
      <div
        aria-hidden={mode === 'tracking' && !resultsOpen ? true : undefined}
        className={`flex min-w-0 flex-col ${
          mode !== 'tracking' || (resultsOpen && !mapOpen) ? 'flex-1'
            : !resultsOpen ? 'w-0 shrink-0 overflow-hidden'
            : `shrink-0 ${trackDragging ? 'select-none' : ''}`
        }`}
        style={mode === 'tracking' && resultsOpen && mapOpen ? { width: trackWidth } : undefined}
      >
        {/* What IS selected, in one line, individually removable. NEVER folded: see `panelOpen`. */}
        {nActive > 0 && (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-border bg-card px-4 py-2">
            {(filters.time.preset !== 'today' || filters.time.from) && (
              <ActiveChip label={timeSummary} onRemove={() => set('time', { preset: 'today' })} />
            )}
            {/* The dragged range appears here like every other narrowing, so it can be undone
                from the filter bar and is never a hidden reason the results look short. */}
            {filters.range && (
              <ActiveChip label={`Range: ${rangeSummary}`} onRemove={() => setRange(null)} />
            )}
            {filters.groups.map((id) => (
              <ActiveChip
                key={id}
                label={`Group: ${groupNames.get(id) || id.slice(0, 8)}`}
                onRemove={() => set('groups', filters.groups.filter((g) => g !== id))}
              />
            ))}
            {filters.cameras.map((id) => (
              <ActiveChip
                key={id}
                label={`Camera: ${cameraNames.get(id) || id.slice(0, 8)}`}
                onRemove={() => set('cameras', filters.cameras.filter((c) => c !== id))}
              />
            ))}
            {/* Only shown when it differs from the default, so the chip row reports a CHOICE
                rather than restating the page's own starting state.

                Gated on `subjectModels` rather than on the models actually searched: an identity
                filter widens the set by itself, and a chip for that would offer a "remove" that
                clears a list which is already empty and changes nothing. That widening is
                stated in the Identity panel and in the "Detected by" summary instead. */}
            {filters.subjectModels.length > 0
              && activeModels.length !== subjectDef.defaultModels.length && (
              <ActiveChip
                label={`Detected by: ${activeModels.map((m) => MODEL_LABEL[m] || titleCase(m)).join(' + ')}`}
                onRemove={() => set('subjectModels', [])}
              />
            )}
            {filters.otherModels.map((m) => (
              <ActiveChip
                key={m}
                label={MODEL_LABEL[m] || titleCase(m)}
                onRemove={() => set('otherModels', filters.otherModels.filter((x) => x !== m))}
              />
            ))}
            {/* Only the ACTIVE subject's attributes: a colour still selected on the Person tab is
                not part of a Vehicle query, so showing it as active would misdescribe the search. */}
            {subjectDef.fields.flatMap((field) => (filters.attributes[field] || []).map((v) => (
              <ActiveChip
                key={`${field}:${v}`}
                adornment={<Swatch value={v} />}
                label={`${FIELD_LABEL[field] || titleCase(field)}: ${titleCase(v)}`}
                onRemove={() => setAttribute(field, (filters.attributes[field] || []).filter((x) => x !== v))}
              />
            )))}
            {subjectDef.textFilter === 'person' && filters.known !== null && (
              <ActiveChip
                label={filters.known ? 'Enrolled people' : 'Unidentified only'}
                onRemove={() => set('known', null)}
              />
            )}
            {subjectDef.textFilter === 'person' && filters.personName.trim() && (
              <ActiveChip
                label={`Name: ${filters.personName.trim()}`}
                onRemove={() => set('personName', '')}
              />
            )}
            {subjectDef.textFilter === 'plate' && filters.plate.trim() && (
              <ActiveChip
                label={`Plate: ${filters.plate.trim().toUpperCase()}`}
                onRemove={() => set('plate', '')}
              />
            )}
            {textSearch && filters.text.trim() && (
              <ActiveChip
                label={`Text: “${filters.text.trim()}”`}
                onRemove={() => set('text', '')}
              />
            )}
            {filters.watchlist !== 'any' && (
              <ActiveChip
                label={filters.watchlist === 'not_listed'
                  ? 'Not on any watch list'
                  : filters.watchlistCategories.length
                    ? `Watch list: ${filters.watchlistCategories.map((c) => titleCase(c)).join(', ')}`
                    : 'On a watch list'}
                onRemove={() => setFilters((prev) => ({
                  ...prev, watchlist: 'any', watchlistCategories: [],
                }))}
              />
            )}
          </div>
        )}

        {/* Every spelling the fuzzy text matched, each one strikable. Shown whenever the answer
            came from text, so no match is ever made silently. */}
        {textSearch && !!result?.text_matches?.length && (
          <TextMatchesBar
            report={result.text_matches}
            excluded={filters.excludedTerms}
            fuzziness={filters.fuzziness}
            onToggle={toggleSpelling}
            onFuzziness={(f: SearchFuzziness) => { setPage(0); setFilters((prev) => ({ ...prev, fuzziness: f, excludedTerms: [] })); }}
          />
        )}

      {/* ── Status. The figures now live in each answer, so this is only what the answer cannot
             say about itself: whether it is loading, whether it failed, and how long it took. ── */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-4 py-2 text-sm">
        {loading ? (
          <span className="text-muted-foreground">Searching…</span>
        ) : error ? (
          <span className="flex items-center gap-1.5 text-red-500">
            <ExclamationTriangleIcon className="h-4 w-4" />
            {error}
          </span>
        ) : (
          <>
            {/* An empty answer and an UNANSWERABLE one look identical, and the difference is the
                whole point of the identity filter: "she was not here" versus "this search cannot
                see who anybody is". Said in the status line, not only inside the panel that
                caused it, because nobody reopens a dropdown to find out why a result is empty. */}
            {/* Text that cannot be searched is left out of the query (sending it would turn every
                half-typed keystroke into an error) — so the page has to say so, or "14:05" in
                the text box would quietly search everything. */}
            {textSearch && filters.text.trim() && !searchableText(filters.text) && (
              <span className="flex items-center gap-1.5 text-amber-500">
                <ExclamationTriangleIcon className="h-4 w-4" />
                “{filters.text.trim()}” has nothing to search for — words need 2+ letters or digits,
                and clock times are not indexed (use the time filter).
              </span>
            )}
            {identityDead && (
              <span className="flex items-center gap-1.5 text-amber-500">
                <ExclamationTriangleIcon className="h-4 w-4" />
                “Detected by” has no model that records identity, so this{' '}
                {subjectDef.textFilter === 'plate' ? 'plate' : 'name'} cannot match anything.
              </span>
            )}
            {/*
              "Not today" instead of "not here" — see `widerWindow`. Rendered in the status line
              rather than inside the empty result, because the empty result belongs to whichever
              answer is showing and this sentence is true of all six.
            */}
            {widerWindow && (
              <span className="flex items-center gap-1.5 text-foreground">
                <span className="text-muted-foreground">
                  Nothing {searchedNoun} in this window —{' '}
                  <strong className="text-foreground">{widerWindow.events}</strong>{' '}
                  {widerWindow.events === 1 ? 'detection' : 'detections'} in the last 30 days.
                </span>
                <button
                  type="button"
                  onClick={() => set('time', { preset: 'last_30_days' })}
                  className="font-medium text-brand hover:underline"
                >
                  Search the last 30 days
                </button>
              </span>
            )}
            {/* The list is the one answer with no headline of its own. */}
            {filters.question === 'list' && (
              <>
                <span className="text-foreground">
                  <strong>{total}</strong> {total === 1 ? 'detection' : 'detections'}
                </span>
                {typeof distinct === 'number' && (
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">{distinct}</strong>{' '}
                    {distinct === 1 ? 'distinct person' : 'distinct people'}
                  </span>
                )}
                {/* Said out loud, not left blank. The server skips the distinct-subject count on
                    a wide unfiltered window because it costs seconds there — and a missing number
                    that looks like an absent one would be read as zero. */}
                {result?.distinct_omitted && (
                  <span
                    className="text-muted-foreground"
                    title="Counting distinct people across this many events takes seconds, so it was skipped. Narrow the window or add a filter and the count returns."
                  >
                    distinct people <strong className="text-foreground">—</strong>{' '}
                    <span className="text-xs">(too many events to count; narrow the window)</span>
                  </span>
                )}
                {typeof result?.camera_scope === 'number' && (
                  <span className="text-xs text-muted-foreground">
                    across {result.camera_scope} {result.camera_scope === 1 ? 'camera' : 'cameras'}
                  </span>
                )}
              </>
            )}
            {filters.question !== 'list' && (
              <span className="text-xs text-muted-foreground">{questionSpec.help}</span>
            )}
          </>
        )}

        {/* ── The right-hand group: how long it took, and how the list is drawn.
               One container rather than two `ml-auto` items, which would have split the free
               space between them and left the timing floating in the middle of the bar. ── */}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {!loading && !error && typeof result?.took_ms === 'number' && (
            <span className="text-xs text-muted-foreground">{result.took_ms} ms</span>
          )}

          {/* Reading direction — only where a trail is actually drawn, which is the Track
              mode's folded list. Newest-first by default, because that is the order the server
              pages in and the order the pager below counts in; it reverses only the rows
              already on screen, which the button's title says out loud.

              The Timeline / Grid toggle that used to sit beside it is gone: it was the mode
              written a second time, and the header now owns that question. */}
          {/* Cards per row. Auto fits the cards to the height; a number splits the width. */}
          {mode === 'tracking' && filters.question === 'list' && (
            <div
              role="radiogroup"
              aria-label="Cards per row"
              className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted p-0.5"
            >
              {[0, 1, 2, 3, 4].map((n) => {
                const on = trackPerRow === n;
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={n ? `${n} ${n === 1 ? 'card' : 'cards'} per row` : 'Auto card size'}
                    onClick={() => chooseTrackPerRow(n)}
                    title={n
                      ? `${n} ${n === 1 ? 'card' : 'cards'} per row — fewer if the column is too narrow`
                      : 'Auto — fit the cards to the space'}
                    className={`flex h-6 items-center justify-center rounded-md transition-colors ${
                      n ? 'w-7' : 'px-2'
                    } ${on
                      ? 'bg-card text-brand shadow-sm ring-1 ring-border'
                      : 'text-muted-foreground hover:bg-card hover:text-foreground'}`}
                  >
                    {n ? <ColumnsGlyph n={n} /> : <span className="text-[10px] font-semibold uppercase tracking-wide">Auto</span>}
                  </button>
                );
              })}
            </div>
          )}
          {mode === 'tracking' && filters.question === 'list' && (
            <button
              type="button"
              onClick={toggleOrder}
              className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-foreground hover:bg-muted"
              title="Reverses the rows on this page only — the server still pages newest-first"
            >
              <ArrowsUpDownIcon className="h-3.5 w-3.5" />
              {trailOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            </button>
          )}
          {mode === 'tracking' && (
            <HidePaneButton side="right" label="track results" onClick={toggleResults} />
          )}
        </div>
      </div>

      {/* ── The answer ──

             `staleShape` is the guard that keeps a question and an answer from disagreeing on
             screen. The previous answer is deliberately left up while the next one loads —
             blanking the page on every filter keystroke is worse — but that is only safe while
             the two are answers to the SAME question. Across a change of question they are
             different row SHAPES, and handing the new question's component the old question's
             rows is a crash, not a flicker. ── */}
      {/* `min-h-[9rem]` rather than `min-h-0`: this is the one row that gives its height away
             as the dock below grows, and a floor of zero means it can be given away ENTIRELY —
             and then the column overflows and the dock's own bottom edge goes off the page. The
             floor is what the dock measures itself against; see `measureCeiling`. It is still
             far below the content height, so `overflow-auto` still scrolls. */}
      <div className="min-h-[9rem] flex-1 overflow-auto">
        {(loading || staleShape) && !answerReady ? (
          <div className="flex h-full items-center justify-center"><LoadingSpinner /></div>
        ) : (
          renderAnswer()
        )}
      </div>

      {/* ── Paging. Only the two answers that HAVE pages: a place roll-up and a timeline are
             bounded by the estate and the window, and offering Next on them would offer a page
             that is always empty. Deliberately prev/next only — a numbered pager over a result
             set that changes as events arrive invites the reader to trust a page number that
             will not mean the same thing a minute later. ── */}
      {questionSpec.paged && (page > 0 || rows.length === limit) && (
        <div className="flex shrink-0 items-center justify-center gap-3 border-t border-border px-4 py-2 text-xs">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-md border border-border px-2 py-1 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-muted-foreground">
            {`${page * limit + 1}–${page * limit + rows.length} of ${total}`}
          </span>
          <button
            type="button"
            disabled={rows.length < limit}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-md border border-border px-2 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {/* Track's path is no longer docked here — it is the centre pane; see `mapOpen`. Search
             has no dock, so its answer gets the full height. */}

      {/* ── The open record.
             A modal rather than a route, because a search is a position the operator is working
             through: navigating away throws the query, the answer and the scroll position away to
             look at one hit. Same shell and same body the report tables use, so a record found by
             search and a record found by report are the same record. ── */}
      {eventDetail && (
        <RecordModal<SearchEventRow>
          rows={detailRows}
          index={detailIndex}
          onIndexChange={setDetailIndex}
          onClose={() => setDetailIndex(null)}
          load={eventDetail.load}
          /**
           * (model, event_id) — NOT the subject. A frame of seven people is seven rows sharing
           * one event, and they resolve to one typed record; keying on the subject too would
           * refetch the identical record on every ← / → step within a frame.
           */
          identity={(row) => `${row.model_key}:${row.event_id}`}
          title={eventDetail.title}
          fallbackTitle={(row) => `${MODEL_LABEL[row.model_key] || titleCase(row.model_key || '')} event`}
          subtitle={(row) => [
            cameraNames.get(row.camera_id) || row.camera_id,
            row.event_timestamp ? new Date(row.event_timestamp).toLocaleString() : null,
          ].filter(Boolean).join(' · ')}
          noun="results"
          onOpenFull={onOpenEvent}
          render={(event, row) => eventDetail.render(event, row, {
            cameraNames: cameraNameMap,
            attributeFacts: attributeFacts(filters.subject, subjectDef, row),
          })}
        />
      )}

      {/* ── Docked playback. Overlays the grid rather than replacing it, so an operator can
             play one hit, dismiss it and try the next without losing the query or the scroll
             position. Keyed on the event id so switching cards remounts the video rather than
             re-pointing a playing element at a different source. ── */}
      {playbackRow && playback && (() => {
        const win = playbackWindow(playbackRow);
        if (!win) return null;
        const camera = cameraNames.get(playbackRow.camera_id) || playbackRow.camera_id.slice(0, 8);
        const label = MODEL_LABEL[playbackRow.model_key] || titleCase(playbackRow.model_key || '');
        return (
          <SearchPlaybackPanel
            key={playbackRow.event_id}
            title={camera}
            subtitle={label}
            src={search.playbackUrl(playbackRow.camera_id, win.from, win.to)}
            leadSeconds={PLAYBACK_LEAD_SEC}
            tailSeconds={PLAYBACK_TAIL_SEC}
            eventMs={new Date(playbackRow.event_timestamp).getTime()}
            windowMs={{ from: new Date(win.from).getTime(), to: new Date(win.to).getTime() }}
            /* The segments the playlist is made of, so the panel knows where the clip sits
               inside them. The segments endpoint returns rows that START in its window, the
               playlist every row that OVERLAPS it — asked for the 20 s window itself it returns
               nothing (the clip is mid-segment). So ask a segment-and-a-bit wider each side;
               the panel's offset table then keeps exactly the rows overlapping `windowMs`. */
            loadSegments={() => wallPlayback.getSegments(
              playbackRow.camera_id,
              new Date(new Date(win.from).getTime() - SEGMENT_PAD_MS).toISOString(),
              new Date(new Date(win.to).getTime() + SEGMENT_PAD_MS).toISOString(),
            )}
            tunnelled={playbackTunnelled}
            // Sourced from the adapter, which is where each host's auth already lives. Optional:
            // a host whose playback URL carries its own token needs no header.
            getAuthToken={search.playbackAuthToken ? () => search.playbackAuthToken!() : undefined}
            onOpenFull={onOpenRecordings
              ? () => onOpenRecordings(playbackRow, win.from, win.to)
              : undefined}
            onClose={() => setPlaybackRow(null)}
          />
        );
      })()}

      </div>
      </div>
    </div>
  );
}
